// src/pages/BusinessDashboard.tsx
import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../supabaseClient';
import {
  FaSignOutAlt, FaStore, FaUserEdit, FaHome,
  FaCamera, FaBullhorn, FaPlus, FaTrash, FaCheckCircle,
  FaClock, FaGem, FaLock, FaChartBar, FaEye, FaMousePointer,
  FaCalendarCheck, FaImages, FaExternalLinkAlt, FaRobot, FaMagic, FaMicrophone,
  FaLightbulb, FaSmile, FaBriefcase, FaHandshake as FaHandshakeIcon, FaGlassCheers, FaVideo, FaFacebook, FaInstagram, FaTiktok,
  FaEnvelope, FaGift, FaQrcode, FaInfoCircle, FaArrowRight, FaCoins,
  FaFire, FaStopwatch, FaBoxOpen, FaTruck, FaShoppingBag, FaSearch, FaPrint, FaUtensils, FaBed, FaTag,
  FaWifi, FaShower, FaKey, FaGlobe, FaMapMarkerAlt, FaPhone, FaShippingFast, FaCheck, FaCrown, FaCheckDouble,
  FaCalendarAlt, FaLink, FaHotel, FaListAlt, FaTimes, FaBan, FaPaperPlane, FaSync, FaRulerCombined, FaWeightHanging,
  FaUsers,
  FaQuestionCircle,
  FaEyeSlash,
  FaSwimmingPool, FaSpa, FaConciergeBell, FaTv, FaParking, FaSnowflake, FaDog, FaSmokingBan, FaWineGlassAlt,
  FaSave, FaStar, FaGavel, FaFileSignature, FaPenFancy, FaFileContract, FaSignature, FaDownload, FaCopy, FaHistory,
  FaTimesCircle, FaExclamationTriangle 
} from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatSystem from '../components/ChatSystem';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import { redirectToSubscription } from '../services/stripePayment';
import {
  redeemMerchantVoucher,
  validateMerchantVoucher,
} from '../lib/vouchers';

// ✅ IMPORTS DES SECTIONS MODULAIRES
import BusinessDiningSection from '../components/business/BusinessDiningSection';
import UpsellEngineSection from '../components/business/UpsellEngineSection';

// ======================
// CONSTANTES SÉCURISÉES
// ======================
const CATEGORIES = [
  "Restauration", "Hébergement", "Activité & Loisir", "Service Local",
  "Transport", "Boutique & Mode", "Santé & Beauté", "Événementiel",
  "Art & Culture", "Vie Nocturne", "Autre"
];

const HOTEL_AMENITIES_OPTIONS: Record<string, { label: string; icon: React.ReactNode }[]> = {
  "Confort": [
    { label: "Wi-Fi Gratuit", icon: <FaWifi /> },
    { label: "Climatisation", icon: <FaSnowflake /> },
    { label: "Chauffage", icon: <FaFire /> },
  ],
  "Bien-être & Loisir": [
    { label: "Piscine", icon: <FaSwimmingPool /> },
    { label: "Spa & Bien-être", icon: <FaSpa /> },
    { label: "Salle de sport", icon: <FaWeightHanging /> },
  ],
  "Services": [
    { label: "Réception 24/24", icon: <FaConciergeBell /> },
    { label: "Petit-déjeuner inclus", icon: <FaUtensils /> },
    { label: "Service de chambre", icon: <FaConciergeBell /> },
    { label: "Animaux acceptés", icon: <FaDog /> },
  ],
  "Chambre": [
    { label: "Télévision", icon: <FaTv /> },
    { label: "Sèche-cheveux", icon: <FaMagic /> },
    { label: "Fer à repasser", icon: <FaTiktok /> },
    { label: "Bureau", icon: <FaBriefcase /> }
  ]
};

const CONTRACT_TEMPLATES = [
  { id: 'service', label: 'Contrat de Services', icon: <FaBriefcase /> },
  { id: 'partnership', label: 'Partenariat Commercial', icon: <FaHandshakeIcon /> },
  { id: 'booking', label: 'Contrat de Réservation', icon: <FaCalendarCheck /> },
  { id: 'employment', label: 'Contrat de Travail', icon: <FaUsers /> },
  { id: 'rental', label: 'Contrat de Location', icon: <FaKey /> },
  { id: 'custom', label: 'Contrat Personnalisé', icon: <FaFileContract /> },
];

const ROOM_PLACEHOLDER = 'https://placehold.co/400x300?text=Chambre';

// Helpers anti-crash (IDs parfois number)
const toStr = (v: any) => (v === null || v === undefined ? '' : String(v));
const shortId = (v: any) => toStr(v).slice(0, 8).toUpperCase();
const toNum = (v: any, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// Cover image robust
const getRoomCover = (room: any) =>
  room?.images?.[0] ||
  room?.image_url ||
  room?.cover_url ||
  room?.photo_url ||
  room?.main_image_url ||
  ROOM_PLACEHOLDER;

const COLOR_MAP: Record<string, { text: string; soft: string }> = {
  blue: { text: 'text-blue-500', soft: 'bg-blue-50' },
  green: { text: 'text-green-500', soft: 'bg-green-50' },
  purple: { text: 'text-purple-500', soft: 'bg-purple-50' },
  orange: { text: 'text-orange-500', soft: 'bg-orange-50' },
  red: { text: 'text-red-500', soft: 'bg-red-50' },
  yellow: { text: 'text-yellow-500', soft: 'bg-yellow-50' },
  slate: { text: 'text-slate-500', soft: 'bg-slate-50' },
  indigo: { text: 'text-indigo-500', soft: 'bg-indigo-50' },
};

// --- COMPOSANTS REUTILISABLES ---
const StatCard = ({ title, value, icon: Icon, color }: any) => {
  const c = COLOR_MAP[color] || COLOR_MAP.slate;
  return (
    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
      <div className={`absolute -right-4 -top-4 w-24 h-24 rounded-full ${c.soft} opacity-50 group-hover:scale-110 transition-transform`}></div>
      <Icon className={`absolute right-4 top-4 ${c.text} opacity-20 group-hover:opacity-40 transition-opacity`} size={48} />
      <div className="relative z-10">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">{title}</p>
        <p className="text-3xl font-black text-slate-900 flex items-center gap-2">
          {value}
          <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-1 rounded-full">
            {typeof value === 'number' && value > 0 ? '↑' : ''}
          </span>
        </p>
      </div>
    </div>
  );
};

const AiMagicButton = ({ onClick, loading, label = "Générer Description" }: any) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="absolute right-2 top-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-[10px] uppercase font-black px-3 py-1.5 rounded-full flex items-center gap-1 hover:shadow-lg transition-all disabled:opacity-50 animate-pulse-slow"
  >
    {loading ? <FaSync className="animate-spin" /> : <FaMagic className="text-yellow-300" />}
    {loading ? "..." : label}
  </button>
);

// --- COMPOSANT FORFEO SIGN ---
const ForfeoSignSection = ({ business }: { business: any }) => {
  const [activeSignTab, setActiveSignTab] = useState<'create' | 'sent' | 'received'>('create');
  const [contractsList, setContractsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  
  const [contractForm, setContractForm] = useState({
    template: 'service',
    recipientEmail: '',
    recipientName: '',
    recipientCompany: '',
    subject: '',
    content: '',
    aiPrompt: '',
    expiryDays: 7,
    requireSignature: true,
    generateWithAI: false
  });

  const [signingContract, setSigningContract] = useState<any>(null);
  const [signatureData, setSignatureData] = useState('');

  useEffect(() => {
    fetchContracts();
  }, [business?.id]);

  const fetchContracts = async () => {
    if (!business?.id) return;
    const { data, error } = await supabase
      .from('forfeo_sign_contracts')
      .select('*')
      .eq('business_id', business.id)
      .order('created_at', { ascending: false });
    
    if (!error && data) {
      setContractsList(data);
    }
  };

  const generateContractWithAI = async () => {
    if (!contractForm.aiPrompt?.trim()) {
      alert("Veuillez décrire le contrat souhaité");
      return;
    }

    setAiLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-contract', {
        body: {
          prompt: contractForm.aiPrompt,
          template: contractForm.template,
          businessName: business?.name || "Forfeo Explore",
          recipientName: contractForm.recipientName || "",
          recipientCompany: contractForm.recipientCompany || "",
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Erreur de génération");

      setContractForm((prev: any) => ({
        ...prev,
        subject: data.subject || prev.subject,
        content: data.content || prev.content,
      }));
    } catch (err: any) {
      console.error("generateContractWithAI error:", err);
      alert(`Erreur IA: ${err?.message || "Erreur de génération"}`);
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateContract = async () => {
    if (!contractForm.recipientEmail || !contractForm.content) {
      alert("Email du destinataire et contenu requis");
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data: contract, error } = await supabase
        .from('forfeo_sign_contracts')
        .insert({
          business_id: business.id,
          template_type: contractForm.template,
          recipient_email: contractForm.recipientEmail,
          recipient_name: contractForm.recipientName,
          recipient_company: contractForm.recipientCompany,
          subject: contractForm.subject || `Contrat - ${business?.name}`,
          content: contractForm.content,
          status: 'pending',
          expiry_date: new Date(Date.now() + contractForm.expiryDays * 24 * 60 * 60 * 1000).toISOString(),
          require_signature: contractForm.requireSignature,
          created_by: session?.user?.id
        })
        .select()
        .single();

      if (error) throw error;

      const emailResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/forfeo-sign-send-contract-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contractId: contract.id,
          recipientEmail: contractForm.recipientEmail,
          recipientName: contractForm.recipientName,
          businessName: business?.name,
          subject: contractForm.subject,
          signUrl: `${window.location.origin}/sign/${contract.id}`
        })
      });

      if (!emailResponse.ok) {
        console.warn("Email non envoyé, mais contrat créé");
      }

      alert("✅ Contrat créé et envoyé par email !");
      setContractForm({
        template: 'service',
        recipientEmail: '',
        recipientName: '',
        recipientCompany: '',
        subject: '',
        content: '',
        aiPrompt: '',
        expiryDays: 7,
        requireSignature: true,
        generateWithAI: false
      });
      fetchContracts();
      setActiveSignTab('sent');
    } catch (error: any) {
      alert("Erreur: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignContract = async (
    contractId: string,
  ) => {
    if (!signatureData) {
      alert(
        "Veuillez dessiner votre signature"
      );
      return;
    }

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const { data, error } =
        await supabase.functions.invoke(
          'sign-contract',
          {
            body: {
              contractId,
              signatureData,
              signedBy:
                session?.user?.email ||
                signingContract?.recipient_email ||
                'Signataire',
            },
          },
        );

      if (
        error ||
        data?.success === false
      ) {
        throw new Error(
          data?.error ||
          error?.message ||
          "Signature impossible"
        );
      }

      alert(
        "✅ Contrat signé avec succès !"
      );

      setSigningContract(null);
      setSignatureData('');
      await fetchContracts();
    } catch (error: any) {
      alert(
        "Erreur signature: " +
        (
          error?.message ||
          "Erreur inconnue"
        )
      );
    }
  };

  const cancelContract = async (
    contractId: string,
  ) => {
    const { data, error } =
      await supabase.functions.invoke(
        'manage-sign-contract',
        {
          body: {
            action: 'cancel',
            contractId,
          },
        },
      );

    if (
      error ||
      data?.success === false
    ) {
      throw new Error(
        data?.error ||
        error?.message ||
        "Annulation impossible"
      );
    }

    return data;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'signed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'expired': return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'signed': return 'Signé';
      case 'pending': return 'En attente';
      case 'expired': return 'Expiré';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 rounded-[2.5rem] shadow-xl text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-black mb-2 flex items-center gap-3">
              <FaFileSignature className="text-yellow-300" /> Forfeo Sign
            </h2>
            <p className="text-indigo-100">Générez et envoyez des contrats avec signature électronique sécurisée.</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black">{contractsList.filter(c => c.status === 'pending').length}</div>
            <div className="text-xs uppercase tracking-widest opacity-80">En attente</div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 p-1 bg-slate-100 rounded-2xl w-fit">
        {[
          { id: 'create', label: 'Nouveau Contrat', icon: <FaPlus /> },
          { id: 'sent', label: 'Envoyés', icon: <FaPaperPlane /> },
          { id: 'received', label: 'À signer', icon: <FaSignature /> }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSignTab(tab.id as any)}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all ${
              activeSignTab === tab.id 
                ? 'bg-white text-indigo-600 shadow-md' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {activeSignTab === 'create' && (
        <div className="bg-white p-8 rounded-[2.5rem] shadow-lg border border-slate-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                <FaFileContract className="text-indigo-600" /> Configuration
              </h3>
              
              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Type de contrat</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CONTRACT_TEMPLATES.map(template => (
                    <button
                      key={template.id}
                      onClick={() => setContractForm({ ...contractForm, template: template.id })}
                      className={`p-4 rounded-2xl border-2 text-left transition-all ${
                        contractForm.template === template.id
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="text-2xl mb-2">{template.icon}</div>
                      <div className="font-bold text-sm">{template.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Email destinataire *</label>
                  <input
                    type="email"
                    value={contractForm.recipientEmail}
                    onChange={e => setContractForm({ ...contractForm, recipientEmail: e.target.value })}
                    className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 font-bold"
                    placeholder="client@entreprise.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Nom du destinataire</label>
                  <input
                    type="text"
                    value={contractForm.recipientName}
                    onChange={e => setContractForm({ ...contractForm, recipientName: e.target.value })}
                    className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 font-bold"
                    placeholder="Jean Dupont"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Entreprise destinataire</label>
                <input
                  type="text"
                  value={contractForm.recipientCompany}
                  onChange={e => setContractForm({ ...contractForm, recipientCompany: e.target.value })}
                  className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 font-bold"
                  placeholder="Nom de l'entreprise"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Expire dans (jours)</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={contractForm.expiryDays}
                    onChange={e => setContractForm({ ...contractForm, expiryDays: parseInt(e.target.value) })}
                    className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 font-bold text-center"
                  />
                </div>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border-2 border-slate-100">
                  <input
                    type="checkbox"
                    id="requireSignature"
                    checked={contractForm.requireSignature}
                    onChange={e => setContractForm({ ...contractForm, requireSignature: e.target.checked })}
                    className="w-5 h-5 text-indigo-600 rounded"
                  />
                  <label htmlFor="requireSignature" className="font-bold text-sm cursor-pointer">
                    Requérir une signature
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                <FaRobot className="text-purple-600" /> Contenu du contrat
              </h3>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-2xl border border-purple-100">
                <label className="block text-xs font-black text-purple-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                  <FaMagic /> Générer avec l'IA
                </label>
                <textarea
                  value={contractForm.aiPrompt}
                  onChange={e => setContractForm({ ...contractForm, aiPrompt: e.target.value })}
                  placeholder="Décrivez le contrat souhaité (ex: Contrat de prestation de services...)"
                  className="w-full p-4 rounded-xl bg-white border border-purple-200 font-medium text-sm h-24 resize-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                />
                <button
                  onClick={generateContractWithAI}
                  disabled={aiLoading || !contractForm.aiPrompt.trim()}
                  className="mt-3 w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-xl font-black uppercase tracking-widest hover:shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {aiLoading ? <FaSync className="animate-spin" /> : <FaMagic />}
                  {aiLoading ? 'Génération...' : 'Générer le contrat'}
                </button>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Objet du contrat</label>
                <input
                  type="text"
                  value={contractForm.subject}
                  onChange={e => setContractForm({ ...contractForm, subject: e.target.value })}
                  className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 font-bold"
                  placeholder="Objet du contrat"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Contenu *</label>
                <textarea
                  value={contractForm.content}
                  onChange={e => setContractForm({ ...contractForm, content: e.target.value })}
                  className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 font-medium h-64 resize-none"
                  placeholder="Le contenu du contrat apparaîtra ici..."
                />
              </div>

              <button
                onClick={handleCreateContract}
                disabled={loading || !contractForm.recipientEmail || !contractForm.content}
                className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-slate-800 transition shadow-lg flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {loading ? <FaSync className="animate-spin" /> : <FaPaperPlane />}
                {loading ? 'Envoi...' : 'Envoyer le contrat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeSignTab === 'sent' && (
        <div className="bg-white p-8 rounded-[2.5rem] shadow-lg border border-slate-100">
          <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
            <FaHistory className="text-indigo-600" /> Contrats envoyés
          </h3>
          
          {contractsList.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
              <FaFileSignature className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500 font-bold">Aucun contrat envoyé</p>
              <button 
                onClick={() => setActiveSignTab('create')}
                className="mt-4 text-indigo-600 font-bold hover:underline"
              >
                Créer votre premier contrat
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {contractsList.map(contract => (
                <div key={contract.id} className="bg-slate-50 p-6 rounded-2xl border border-slate-100 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-black text-lg text-slate-900">{contract.subject}</h4>
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${getStatusColor(contract.status)}`}>
                          {getStatusLabel(contract.status)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">
                        À: <strong>{contract.recipient_name || contract.recipient_email}</strong>
                        {contract.recipient_company && ` • ${contract.recipient_company}`}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-400 font-bold">
                        <span className="flex items-center gap-1">
                          <FaCalendarAlt /> {new Date(contract.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <FaClock /> Expire le {new Date(contract.expiry_date).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <FaFileContract /> {CONTRACT_TEMPLATES.find(t => t.id === contract.template_type)?.label || contract.template_type}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => navigator.clipboard.writeText(`${window.location.origin}/sign/${contract.id}`)}
                        className="p-3 bg-white rounded-xl border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300 transition"
                        title="Copier le lien"
                      >
                        <FaCopy />
                      </button>
                      <button 
                        onClick={() => window.open(`${window.location.origin}/sign/${contract.id}`, '_blank')}
                        className="p-3 bg-white rounded-xl border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300 transition"
                        title="Voir le contrat"
                      >
                        <FaEye />
                      </button>
                      {contract.status === 'pending' && (
                        <button 
                          onClick={async () => {
                            if (!confirm('Annuler ce contrat ?')) return;
                            try {
                              await cancelContract(
                                contract.id
                              );

                              await fetchContracts();
                            } catch (error: any) {
                              alert(
                                "Erreur annulation: " +
                                (
                                  error?.message ||
                                  "Erreur inconnue"
                                )
                              );
                            }
                          }}
                          className="p-3 bg-white rounded-xl border border-slate-200 text-slate-600 hover:text-red-600 hover:border-red-300 transition"
                          title="Annuler"
                        >
                          <FaBan />
                        </button>
                      )}
                    </div>
                  </div>
                  {contract.signed_at && (
                    <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-100">
                      <div className="flex items-center gap-2 text-green-700 font-bold text-sm">
                        <FaCheckCircle /> Signé le {new Date(contract.signed_at).toLocaleString()} par {contract.signed_by}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSignTab === 'received' && (
        <div className="bg-white p-8 rounded-[2.5rem] shadow-lg border border-slate-100">
          <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
            <FaSignature className="text-indigo-600" /> Contrats à signer
          </h3>
          
          <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
            <FaEnvelope className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500 font-bold">Les contrats à signer apparaissent ici</p>
            <p className="text-sm text-slate-400 mt-2">Vous recevrez un email lorsqu'un contrat vous est envoyé</p>
          </div>
        </div>
      )}

      {/* Modale de signature */}
      {signingContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl p-8">
            <h3 className="text-2xl font-black mb-4 flex items-center gap-2">
              <FaPenFancy className="text-indigo-600" /> Signer le contrat
            </h3>
            <p className="text-slate-600 mb-6">{signingContract.subject}</p>
            
            <div className="border-2 border-slate-200 rounded-2xl p-4 mb-6 bg-slate-50">
              <p className="text-xs font-bold text-slate-400 uppercase mb-2">Dessinez votre signature</p>
              <canvas
                className="w-full h-32 bg-white rounded-xl cursor-crosshair"
                onMouseDown={(e) => {
                  const canvas = e.currentTarget;
                  const ctx = canvas.getContext('2d');
                  if (!ctx) return;
                  ctx.beginPath();
                  ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
                  const handleMove = (e: MouseEvent) => {
                    ctx.lineTo(e.offsetX, e.offsetY);
                    ctx.stroke();
                  };
                  const handleUp = () => {
                    canvas.removeEventListener('mousemove', handleMove);
                    canvas.removeEventListener('mouseup', handleUp);
                    setSignatureData(canvas.toDataURL());
                  };
                  canvas.addEventListener('mousemove', handleMove);
                  canvas.addEventListener('mouseup', handleUp);
                }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSigningContract(null)}
                className="flex-1 py-3 rounded-xl border-2 border-slate-200 font-bold text-slate-600 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                onClick={() => handleSignContract(signingContract.id)}
                disabled={!signatureData}
                className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-black uppercase tracking-widest hover:bg-indigo-700 transition disabled:opacity-50"
              >
                Confirmer la signature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default function BusinessDashboard() {
  const navigate = useNavigate();

  const [business, setBusiness] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isPosting, setIsPosting] = useState(false);

  // --- ÉTATS GÉNÉRAUX & NAVIGATION ---
  const [activeTab, setActiveTab] = useState<
    'overview' | 'stats' | 'profile' | 'posts' | 'messages' | 'loyalty' | 'lastcall' | 'shop' | 'orders' | 'hotel' | 'wallet' | 'booking' | 'calendar' | 'upsell' | 'sign' | 'boutique' | 'scanner'
  >('overview');
  const [unreadCount, setUnreadCount] = useState(0);
  const [stats, setStats] = useState({ views: 0, clicks: 0, revenue: 0 });

  const isPremium = business?.plan_type === 'premium' || business?.is_pro === true;

  // --- ÉTATS POUR L'IA ET LES CHARGEMENTS ---
  const [aiLoading, setAiLoading] = useState({ hotel: false, deal: false, product: false, post: false, sync: false, mystery: false, boutique: false });
  const [uploading, setUploading] = useState(false);

  const [mysteryImgProcessing, setMysteryImgProcessing] = useState(false);
  const [mysteryPreviewMode, setMysteryPreviewMode] = useState<'public' | 'secret'>('public');
  const [mysteryAdminShowRealById, setMysteryAdminShowRealById] = useState<Record<string, boolean>>({});

  // --- REFS POUR L'UPLOAD DE FICHIERS ---
  const profileFileInputRef = useRef<HTMLInputElement>(null);
  const roomFileInputRef = useRef<HTMLInputElement>(null);
  const dealFileInputRef = useRef<HTMLInputElement>(null);
  const productFileInputRef = useRef<HTMLInputElement>(null);
  const postFileInputRef = useRef<HTMLInputElement>(null);
  const mysteryFileInputRef = useRef<HTMLInputElement>(null);
  const boutiqueFileInputRef = useRef<HTMLInputElement>(null);

  // --- ÉTATS DES DONNÉES (Listes) ---
  const [orders, setOrders] = useState<any[]>([]);
  const [reservations, setReservations] = useState<any[]>([]);
  const [accommodations, setAccommodations] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [rewards, setRewards] = useState<any[]>([]);
  const [mysteryOffers, setMysteryOffers] = useState<any[]>([]);
  const [boutiqueForfaits, setBoutiqueForfaits] = useState<any[]>([]);
  const [expandedMysteryId, setExpandedMysteryId] = useState<string | null>(null);
  const [boutiqueReviews, setBoutiqueReviews] = useState<any[]>([]);

  // ✅ ÉTATS DU SCANNER
  const [scanCode, setScanCode] = useState('');
  const [scanMessage, setScanMessage] = useState<{type: 'success' | 'error' | 'warning', text: string} | null>(null);
  const [scanLoading, setScanLoading] = useState(false);

  const [hotelDetails, setHotelDetails] = useState({
    amenities: [] as string[],
    rules: { check_in: "15:00", check_out: "11:00", pets_allowed: false, smoking_allowed: false, parties_allowed: false, custom_rules: "" },
    faq: [] as { question: string, answer: string }[]
  });
  const [hotelDetailsLoading, setHotelDetailsLoading] = useState(false);

  const [calendarKey, setCalendarKey] = useState(0);

  // --- ÉTATS DES FORMULAIRES ---
  const [formData, setFormData] = useState({
    name: '', description: '', phone: '', address: '', city: '', region: 'Québec', website: '', email: '',
    category: 'Restauration', image_url: '', latitude: '', longitude: '',
    facebook_url: '', instagram_url: '', tiktok_url: ''
  });
  const [newPassword, setNewPassword] = useState({ new: '', confirm: '' });

  const [newRoom, setNewRoom] = useState<{ name: string, description: string, price_per_night: string, capacity_adults: number, images: string[] }>(
    { name: '', description: '', price_per_night: '', capacity_adults: 2, images: [] }
  );

  const [newDeal, setNewDeal] = useState({
    title: '', description: '', original_price: '', discount_price: '', quantity_available: 5, expires_in_hours: 24, image_url: '',
    party_size: 2, slot_start_local: '', slot_end_local: ''
  });

  const [newProduct, setNewProduct] = useState({
    name: '', description: '', price: '', stock_quantity: 10, weight_kg: 0.5, length_cm: 10, width_cm: 10, height_cm: 10, image_url: ''
  });

  const [newBoutiqueForfait, setNewBoutiqueForfait] = useState({
    title: '', description: '', original_price: '', price: '', category: 'Détente',
    includes: '', conditions: '', expiration: '', image_url: ''
  });

  const [newPost, setNewPost] = useState({ content: '', image_url: '' });

  const [newReward, setNewReward] = useState({ title: '', cost: 500, description: '' });

  const [loyaltyScan, setLoyaltyScan] = useState({ id: '', points: 10, message: '' });

  const [newMysteryOffer, setNewMysteryOffer] = useState<any>({
    title: '', description: '', original_price: '', mystery_price: '', quantity_available: 5, expires_in_hours: 72,
    image_url: '', secret_image_url: '', blur_enabled: true, blur_radius: 16, reveal_after_purchase: true,
  });

  const [selectedRes, setSelectedRes] = useState<any>(null);

  const [bookingSettings, setBookingSettings] = useState<any>({ cal_link: '', qlo_link: '', is_booking_enabled: false });

  // ✅ Menu catégorisé pour alléger l'interface
  const MENU_GROUPS = [
    {
      title: "Général",
      items: [
        { id: 'overview', icon: <FaHome />, label: 'Vue d\'ensemble' },
        { id: 'scanner', icon: <FaQrcode />, label: 'Scanner / Valider', special: true }, 
        { id: 'calendar', icon: <FaCalendarCheck />, label: 'Calendrier & Dispos', special: true },
      ]
    },
    {
      title: "Ventes & Opérations",
      items: [
        { id: 'booking', icon: <FaListAlt />, label: 'Réservations', special: true },
        { id: 'orders', icon: <FaShoppingBag />, label: 'Commandes', count: orders.filter(o => o.status === 'paid').length },
        { id: 'upsell', icon: <FaMagic className="text-fuchsia-500" />, label: 'Moteur Upsell', special: true }, // ✅ NOUVEAU
        { id: 'wallet', icon: <FaCoins />, label: 'Portefeuille' },
      ]
    },
    {
      title: "Mon Établissement",
      items: [
        { id: 'hotel', icon: <FaBed />, label: 'Hébergement & Resto' },
        { id: 'boutique', icon: <FaStore />, label: 'Boutique Forfaits' },
        { id: 'lastcall', icon: <FaFire />, label: 'Offres Flash' },
        { id: 'shop', icon: <FaBoxOpen />, label: 'Produits Physiques' },
      ]
    },
    {
      title: "Marketing & Relation",
      items: [
        { id: 'messages', icon: <FaEnvelope />, label: 'Messagerie', count: unreadCount },
        { id: 'posts', icon: <FaBullhorn />, label: 'Publications' },
        { id: 'loyalty', icon: <FaGift />, label: 'Club Fidélité' },
        { id: 'sign', icon: <FaFileSignature />, label: 'Forfeo Sign' },
      ]
    },
    {
      title: "Administration",
      items: [
        { id: 'stats', icon: <FaChartBar />, label: 'Statistiques' },
        { id: 'profile', icon: <FaUserEdit />, label: 'Profil & Paramètres' },
      ]
    }
  ];

  useEffect(() => { fetchBusinessData(); }, []);

  const blobToJpeg = (blob: Blob) =>
    new Promise<ImageBitmap>((resolve, reject) => {
      // @ts-ignore
      if (typeof createImageBitmap === 'function') {
        // @ts-ignore
        createImageBitmap(blob).then(resolve).catch(reject);
      } else {
        reject(new Error("createImageBitmap indisponible"));
      }
    });

  const blurImageUrlToBlob = async (url: string, blurPx: number): Promise<Blob> => {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`Fetch image failed (${res.status})`);
    const srcBlob = await res.blob();
    let bmp: ImageBitmap | null = null;
    try {
      bmp = await blobToJpeg(srcBlob);
    } catch {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error("Impossible de charger l'image pour flou"));
        el.src = URL.createObjectURL(srcBlob);
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error("Canvas 2D non disponible");
      ctx.filter = `blur(${Math.max(0, blurPx)}px)`;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return await new Promise((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error("toBlob() échoué")), 'image/jpeg', 0.92);
      });
    }

    const canvas = document.createElement('canvas');
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("Canvas 2D non disponible");
    ctx.filter = `blur(${Math.max(0, blurPx)}px)`;
    ctx.drawImage(bmp, 0, 0);

    return await new Promise((resolve, reject) => {
      canvas.toBlob((b) => b ? resolve(b) : reject(new Error("toBlob() échoué")), 'image/jpeg', 0.92);
    });
  };

  const uploadBlobToBusinessMedia = async (path: string, blob: Blob) => {
    const { error } = await supabase.storage
      .from('business-media')
      .upload(path, blob, { upsert: true, contentType: 'image/jpeg' });
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('business-media').getPublicUrl(path);
    return publicUrl;
  };

  const generateMysteryBlurredPublicImage = async () => {
    const src = newMysteryOffer.secret_image_url || newMysteryOffer.image_url;
    if (!src) return alert("Ajoute d'abord une image.");
    if (!business?.id) return;
    setMysteryImgProcessing(true);
    try {
      const blurPx = toNum(newMysteryOffer.blur_radius, 16);
      const blurredBlob = await blurImageUrlToBlob(src, blurPx);
      const fileName = `${business.id}/mystery_blur/${Date.now()}-mystery-blur.jpg`;
      const publicUrl = await uploadBlobToBusinessMedia(fileName, blurredBlob);

      setNewMysteryOffer((prev: any) => ({
        ...prev,
        image_url: publicUrl
      }));
      alert("✅ Image floutée générée (publique) !");
    } catch (e: any) {
      alert("❌ Erreur flou: " + (e?.message || 'Erreur inconnue'));
    } finally {
      setMysteryImgProcessing(false);
    }
  };


  // GESTION DES REVENUS STRIPE & PREMIUM
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('success') === 'true' && business && !business.is_hotel_active) {
      supabase
        .from('businesses')
        .update({ is_hotel_active: true, is_pro: true, plan_type: 'premium' })
        .eq('id', business.id)
        .then(({ error }) => {
          if (!error) {
            setBusiness((prev: any) => ({ ...prev, is_hotel_active: true, is_pro: true, plan_type: 'premium' }));
            alert("🎉 Mode Partenaire Premium activé !");
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        });
    }

    if (params.get('connect') === 'success') {
      alert("🎉 Compte bancaire connecté à Stripe avec succès ! Les virements sont actifs.");
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchBusinessData();
    }
  }, [business]);

  // GESTION DES NOTIFICATIONS TEMPS RÉEL
  useEffect(() => {
    if (business?.owner_id && business?.id) {
      const fetchUnread = async () => {
        const { count } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('is_read', false)
          .neq('sender_id', business.owner_id);

        setUnreadCount(count || 0);
      };

      fetchUnread();

      const sub = supabase
        .channel('business-dashboard-realtime')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `receiver_id=eq.${business.owner_id}` }, () => {
          fetchUnread();
          try {
            const audio = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-positive-notification-951.mp3');
            audio.play();
          } catch { }
        })
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'shop_orders', filter: `business_id=eq.${business.id}` }, (payload) => {
          alert(`🔔 Nouvelle commande de ${toNum(payload.new?.total_amount)}$ !`);
          setOrders(prev => [payload.new, ...prev]);
          setStats(prev => ({ ...prev, revenue: prev.revenue + toNum(payload.new?.total_amount) }));
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings', filter: `business_id=eq.${business.id}` }, (payload) => {
          setCalendarKey(prev => prev + 1);
          fetchBusinessData();
          if ((payload as any).eventType === 'INSERT') {
            alert(`🔔 Nouvelle réservation reçue !`);
          }
        })
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'businesses', filter: `id=eq.${business.id}` }, (payload) => {
          if (payload.new && typeof payload.new.views_count === 'number') {
            setStats(prev => ({ ...prev, views: payload.new.views_count }));
          }
        })
        .subscribe();

      return () => { supabase.removeChannel(sub); };
    }
  }, [business]);

  // --- CHARGEMENT DES DONNÉES ---
  const fetchBusinessData = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login'); return; }

      const { data: busData, error: busError } = await supabase
        .from('businesses')
        .select('*')
        .eq('owner_id', session.user.id)
        .single();

      if (busError || !busData) {
        navigate('/register-business');
        return;
      }

      setBusiness(busData);
      setFormData({
        name: busData.name || '',
        description: busData.description || '',
        phone: busData.contact_phone || busData.phone || '',
        address: busData.address || '',
        city: busData.city || '',
        region: busData.region || 'Québec',
        website: busData.website || '',
        email: busData.contact_email || session.user.email || '',
        category: busData.category || 'Restauration',
        image_url: busData.image_url || '',
        latitude: busData.latitude?.toString() || '',
        longitude: busData.longitude?.toString() || '',
        facebook_url: busData.facebook_url || '',
        instagram_url: busData.instagram_url || '',
        tiktok_url: busData.tiktok_url || ''
      });

      setStats(prev => ({
        ...prev,
        views: toNum(busData.views_count),
        clicks: toNum(busData.clicks_count)
      }));

      const hasHotelCols =
        Object.prototype.hasOwnProperty.call(busData, 'hotel_amenities') ||
        Object.prototype.hasOwnProperty.call(busData, 'hotel_rules') ||
        Object.prototype.hasOwnProperty.call(busData, 'hotel_faq');

      if (hasHotelCols) {
        setHotelDetails(prev => ({
          ...prev,
          amenities: Array.isArray(busData.hotel_amenities) ? busData.hotel_amenities : [],
          rules:
            busData.hotel_rules && typeof busData.hotel_rules === 'object'
              ? { ...prev.rules, ...busData.hotel_rules }
              : prev.rules,
          faq: Array.isArray(busData.hotel_faq) ? busData.hotel_faq : [],
        }));
      }

      const bid = busData.id;

      const results = await Promise.all([
        supabase.from('shop_orders').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
        supabase.from('bookings').select('*').eq('business_id', bid).order('check_in_date', { ascending: false }),
        supabase.from('accommodations').select('*').eq('business_id', bid),
        supabase.from('last_call_deals').select('*').eq('business_id', bid).order('expires_at', { ascending: true }),
        supabase.from('shop_products').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
        supabase.from('posts').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
        supabase.from('rewards').select('*').eq('business_id', bid).order('cost_points', { ascending: true }),
        supabase.from('business_booking_settings').select('*').eq('business_id', bid).maybeSingle(),
        supabase.from('mystery_hotel_offers').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
        supabase.from('business_hotel_details').select('*').eq('business_id', bid).maybeSingle(),
        supabase.from('boutique_forfaits').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
        supabase.from('boutique_reviews').select('*, boutique_forfaits!inner(title, business_id)').eq('boutique_forfaits.business_id', bid).order('created_at', { ascending: false })
      ]);

      const ordersData = results[0].data || [];

      const bookingsRaw = results[1].data || [];
      const resData = (bookingsRaw || []).map((r: any) => ({
        ...r,
        start_at: r.start_at || r.check_in_date || null,
        end_at: r.end_at || r.check_out_date || null,
      }));

      const accData = results[2].data || [];
      const dealsData = results[3].data || [];
      const prodData = results[4].data || [];
      const postsData = results[5].data || [];
      const rewardsData = results[6].data || [];
      const settings = results[7].data;

      setOrders(ordersData);
      setReservations(resData);
      setAccommodations(accData);
      setDeals(dealsData);
      setProducts(prodData);
      setPosts(postsData);
      setRewards(rewardsData);

      if (settings) {
        setBookingSettings({
          ...settings,
          cal_link: settings.cal_username ? `https://cal.com/${settings.cal_username}/${settings.cal_event_slug || ''}` : '',
          qlo_link: settings.qlo_endpoint_url || ''
        });
      }

      setMysteryOffers(results[8].data || []);

      const details = results[9].data;
      if (!hasHotelCols && details) {
        setHotelDetails(prev => ({
          ...prev,
          amenities: details.amenities || [],
          rules: details.rules && typeof details.rules === 'object' ? { ...prev.rules, ...details.rules } : prev.rules,
          faq: Array.isArray(details.faq) ? details.faq : []
        }));
      }

      setBoutiqueForfaits(results[10].data || []);
      setBoutiqueReviews(results[11].data || []);

      const ordersRevenue = ordersData
        .filter((o: any) => o.status === 'paid' || o.status === 'picked_up')
        .reduce((sum: number, o: any) => sum + toNum(o.total_amount), 0);

      const resRevenue = resData
        .filter((r: any) => r.payment_status === 'paid')
        .reduce((sum: number, r: any) => sum + toNum(r.total_price), 0);

      setStats(prev => ({ ...prev, revenue: ordersRevenue + resRevenue }));
    } catch (e: any) {
      console.error(e);
      alert("Erreur chargement dashboard : " + (e?.message || 'inconnue'));
    } finally {
      setLoading(false);
    }
  };

  // --- FONCTIONS D'ACTION ---

  const handleValidateVoucher = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const rawCode = scanCode.trim();
    if (!rawCode) {
      alert("Veuillez entrer un code de réservation.");
      return;
    }

    if (!business?.id) {
      alert("Établissement introuvable.");
      return;
    }

    setScanLoading(true);
    setScanMessage(null);

    try {
      const validation = await validateMerchantVoucher(
        rawCode,
        business.id,
        'manual'
      );

      if (!validation?.ok || !validation.voucher) {
        setScanMessage({
          type: 'error',
          text: "Code invalide ou introuvable pour votre établissement."
        });
        return;
      }

      const voucher = validation.voucher;
      const status = String(
        voucher.effective_status || voucher.status || ''
      ).toLowerCase();

      if (status === 'redeemed') {
        setScanMessage({
          type: 'warning',
          text: `Attention : Ce bon a DÉJÀ été utilisé${
            voucher.redeemed_at
              ? ` le ${new Date(voucher.redeemed_at).toLocaleDateString()}`
              : ''
          }.`
        });
        return;
      }

      if (status !== 'active') {
        setScanMessage({
          type: 'error',
          text: `Ce bon ne peut pas être utilisé. Statut : ${status || 'inconnu'}.`
        });
        return;
      }

      const redemption = await redeemMerchantVoucher(
        voucher.secure_token || voucher.voucher_code,
        business.id,
        {
          source: 'manual',
          note: 'Validation depuis le portail Business Forfeo Explore'
        }
      );

      if (!redemption?.ok || !redemption.voucher) {
        throw new Error(redemption?.error || 'redeem_failed');
      }

      const redeemedVoucher = redemption.voucher;
      const order = redeemedVoucher.order || {};
      const client =
        order.customer_name ||
        redeemedVoucher.customer_email ||
        order.customer_email ||
        'Client';

      let itemsDetails =
        redeemedVoucher.title ||
        redeemedVoucher.item?.title ||
        'Forfait / Offre Flash';

      if (Array.isArray(order.items) && order.items.length > 0) {
        itemsDetails = order.items
          .map(
            (item: any) =>
              `${item.quantity || 1}x ${
                item.title || item.name || 'Article'
              }`
          )
          .join(', ');
      }

      setScanMessage({
        type: 'success',
        text: `✅ Succès ! Bon validé pour ${client}. (${itemsDetails})`
      });

      setScanCode('');
      fetchBusinessData();
    } catch (error: any) {
      console.error('Erreur de validation du bon :', error);
      setScanMessage({
        type: 'error',
        text: `Échec de la validation : ${
          error?.message || 'erreur inconnue'
        }`
      });
    } finally {
      setScanLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'room' | 'deal' | 'product' | 'post' | 'mystery' | 'boutique') => {
    if (!e.target.files || e.target.files.length === 0) return;

    setUploading(true);
    try {
      const files = Array.from(e.target.files);
      const roomRemaining = Math.max(0, 10 - (newRoom.images?.length || 0));
      const filesToUpload = type === 'room' ? files.slice(0, roomRemaining) : [files[0]];

      for (const file of filesToUpload) {
        const cleanName = file.name.replace(/[^a-zA-Z0-9.]/g, '');
        const fileName = `${business.id}/${type}/${Date.now()}-${cleanName}`;
        const { error } = await supabase.storage.from('business-media').upload(fileName, file, { upsert: true });
        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage.from('business-media').getPublicUrl(fileName);

        if (type === 'profile') setFormData(prev => ({ ...prev, image_url: publicUrl }));
        else if (type === 'room') setNewRoom(prev => ({ ...prev, images: [...(prev.images || []), publicUrl] }));
        else if (type === 'deal') setNewDeal(prev => ({ ...prev, image_url: publicUrl }));
        else if (type === 'product') setNewProduct(prev => ({ ...prev, image_url: publicUrl }));
        else if (type === 'post') setNewPost(prev => ({ ...prev, image_url: publicUrl }));
        else if (type === 'boutique') setNewBoutiqueForfait(prev => ({ ...prev, image_url: publicUrl }));
        else if (type === 'mystery') {
          setNewMysteryOffer((prev: any) => ({
            ...prev,
            secret_image_url: publicUrl,
            image_url: publicUrl
          }));
          setMysteryPreviewMode('secret');
        }
      }
    } catch (err: any) {
      alert("Erreur d'upload : " + err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const simulateAiGeneration = (field: string, type: 'hotel' | 'deal' | 'product' | 'post' | 'mystery' | 'boutique') => {
    setAiLoading(prev => ({ ...prev, [type]: true }));
    setTimeout(() => {
      const descriptions: any = {
        hotel: "Plongez dans le luxe de notre suite signature...",
        deal: "🔥 OFFRE EXPLOSIVE ! Profitez de -50%...",
        product: "Découvrez l'élégance artisanale...",
        post: "✨ Grande nouvelle !...",
        mystery: "🕵️‍♂️ OFFRE HÔTEL MYSTÈRE : Laissez-vous surprendre...",
        boutique: "Offrez-vous un moment de détente absolue avec ce forfait exclusif..."
      };
      if (type === 'hotel') setNewRoom((prev: any) => ({ ...prev, description: descriptions.hotel }));
      else if (type === 'deal') setNewDeal(prev => ({ ...prev, description: descriptions.deal }));
      else if (type === 'product') setNewProduct(prev => ({ ...prev, description: descriptions.product }));
      else if (type === 'post') setNewPost(prev => ({ ...prev, content: descriptions.post }));
      else if (type === 'boutique') setNewBoutiqueForfait(prev => ({ ...prev, description: descriptions.boutique }));
      else if (type === 'mystery') setNewMysteryOffer((prev: any) => ({ ...prev, description: descriptions.mystery }));
      
      setAiLoading(prev => ({ ...prev, [type]: false }));
    }, 1200);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updates: any = {
        name: formData.name,
        description: formData.description,
        contact_phone: formData.phone,
        address: formData.address,
        city: formData.city,
        region: formData.region,
        website: formData.website,
        contact_email: formData.email,
        category: formData.category,
        image_url: formData.image_url,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        facebook_url: formData.facebook_url,
        instagram_url: formData.instagram_url,
        tiktok_url: formData.tiktok_url
      };

      const { error } = await supabase.from('businesses').update(updates).eq('id', business.id);
      if (error) throw error;

      alert("Profil mis à jour avec succès !");
      setBusiness((prev: any) => ({ ...prev, ...updates }));
    } catch (e: any) {
      alert("Erreur de sauvegarde : " + (e?.message || 'inconnue'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.new !== newPassword.confirm) return alert("Les mots de passe ne correspondent pas.");
    if (newPassword.new.length < 6) return alert("Le mot de passe est trop court (6 car. min).");
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword.new });
      if (error) throw error;
      alert("Mot de passe mis à jour !");
      setNewPassword({ new: '', confirm: '' });
    } catch (e: any) {
      alert("Erreur: " + (e?.message || 'inconnue'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHotelDetails = async () => {
    if (!business?.id) return;
    setHotelDetailsLoading(true);
    try {
      const updates = {
        hotel_amenities: hotelDetails.amenities,
        hotel_rules: hotelDetails.rules,
        hotel_faq: hotelDetails.faq,
      };

      const { error } = await supabase
        .from('businesses')
        .update(updates)
        .eq('id', business.id);

      if (error) throw error;

      setBusiness((prev: any) => ({ ...prev, ...updates }));
      alert("✅ Équipements, Règles et FAQ sauvegardés !");
    } catch (e: any) {
      alert("Erreur sauvegarde : " + (e?.message || "Erreur inconnue"));
    } finally {
      setHotelDetailsLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    if (!window.confirm("Vous allez être redirigé vers Stripe pour connecter votre compte bancaire et recevoir vos paiements.")) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ business_id: business.id, return_url: window.location.origin })
      });
      const data = await response.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || "Erreur inconnue");
    } catch (error: any) {
      alert("Erreur Stripe : " + error.message);
      setLoading(false);
    }
  };

  const handleAddRoom = async () => {
    if (!newRoom.name || !newRoom.price_per_night) return alert("Nom et Prix requis.");

    const base: any = {
      business_id: business.id,
      name: newRoom.name,
      description: newRoom.description,
      price_per_night: parseFloat(newRoom.price_per_night),
      capacity_adults: newRoom.capacity_adults,
      is_available: true,
    };

    const firstImg = (newRoom.images && newRoom.images[0]) ? newRoom.images[0] : null;

    const variants: any[] = [
      { ...base, images: newRoom.images || [], image_url: firstImg },
      { ...base, images: newRoom.images || [] },
      { ...base, image_url: firstImg },
      { ...base },
    ];

    try {
      let lastErr: any = null;

      for (const payload of variants) {
        const { error } = await supabase.from('accommodations').insert(payload);
        if (!error) {
          alert("Chambre ajoutée !");
          setNewRoom({ name: '', description: '', price_per_night: '', capacity_adults: 2, images: [] });
          fetchBusinessData();
          return;
        }

        lastErr = error;
        if (isMissingColumn(error, 'image_url') || isMissingColumn(error, 'images')) continue;
        throw error;
      }

      throw lastErr;
    } catch (e: any) {
      alert("Erreur ajout chambre : " + (e?.message || 'inconnue'));
    }
  };

  const handleCreateDeal = async () => {
    if (!newDeal.title || !newDeal.discount_price) return alert("Titre et Prix réduit requis.");
    try {
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + parseInt(newDeal.expires_in_hours.toString(), 10));

      const payload: any = {
        business_id: business.id,
        title: newDeal.title,
        description: newDeal.description,
        original_price: newDeal.original_price ? parseFloat(newDeal.original_price) : null,
        discount_price: parseFloat(newDeal.discount_price),
        quantity_available: newDeal.quantity_available,
        expires_at: expiresAt.toISOString(),
        image_url: newDeal.image_url || formData.image_url
      };

      const { error } = await supabase.from('last_call_deals').insert(payload);
      if (error) throw error;

      alert("🔥 Offre Flash publiée !");
      setNewDeal({ title: '', description: '', original_price: '', discount_price: '', quantity_available: 5, expires_in_hours: 24, image_url: '' });
      fetchBusinessData();
    } catch (e: any) {
      alert("Erreur Offre Flash : " + (e?.message || 'inconnue'));
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) return alert("Nom et Prix requis.");
    try {
      const payload: any = {
        business_id: business.id,
        name: newProduct.name,
        description: newProduct.description,
        price: parseFloat(newProduct.price),
        stock_quantity: newProduct.stock_quantity,
        weight_kg: newProduct.weight_kg,
        length_cm: newProduct.length_cm,
        width_cm: newProduct.width_cm,
        height_cm: newProduct.height_cm,
        image_url: newProduct.image_url || formData.image_url,
        is_active: true
      };

      const { error } = await supabase.from('shop_products').insert(payload);
      if (error) throw error;

      alert("Produit ajouté à la boutique !");
      setNewProduct({ name: '', description: '', price: '', stock_quantity: 10, weight_kg: 0.5, length_cm: 10, width_cm: 10, height_cm: 10, image_url: '' });
      fetchBusinessData();
    } catch (e: any) {
      alert("Erreur ajout produit : " + (e?.message || 'inconnue'));
    }
  };

  const handleCreateBoutiqueForfait = async () => {
    if (!newBoutiqueForfait.title || !newBoutiqueForfait.price || !newBoutiqueForfait.original_price) {
      return alert("Titre, prix régulier et prix réduit sont requis.");
    }
    
    setSaving(true);
    try {
      const original = parseFloat(newBoutiqueForfait.original_price);
      const price = parseFloat(newBoutiqueForfait.price);
      const discount = Math.round(((original - price) / original) * 100);

      const includesList = newBoutiqueForfait.includes.split('\n').filter(i => i.trim() !== '');
      const conditionsList = newBoutiqueForfait.conditions.split('\n').filter(i => i.trim() !== '');

      const payload = {
        title: newBoutiqueForfait.title,
        business_id: business.id,
        business_name: business.name,
        location: business.city || business.region,
        category: newBoutiqueForfait.category,
        original_price: original,
        price: price,
        discount: discount,
        description: newBoutiqueForfait.description,
        includes: includesList,
        conditions: conditionsList,
        expiration: newBoutiqueForfait.expiration,
        image: newBoutiqueForfait.image_url || business.image_url,
        is_active: true
      };

      const { error } = await supabase.from('boutique_forfaits').insert(payload);
      
      if (error) throw error;
      
      alert("✅ Forfait publié dans la boutique !");
      setNewBoutiqueForfait({
        title: '', description: '', original_price: '', price: '', category: 'Détente',
        includes: '', conditions: '', expiration: '', image_url: ''
      });
      fetchBusinessData();
    } catch (e: any) {
      alert("Erreur: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePublishPost = async () => {
    if (!newPost.content) return alert("Contenu requis.");
    setIsPosting(true);
    try {
      const payload: any = {
        business_id: business.id,
        content: newPost.content,
        image_url: newPost.image_url || null,
      };

      const { error } = await supabase.from('posts').insert(payload);
      if (error) throw error;

      alert("Post publié !");
      setNewPost({ content: '', image_url: '' });
      fetchBusinessData();
    } catch (e: any) {
      alert("Erreur publication : " + (e?.message || 'inconnue'));
    } finally {
      setIsPosting(false);
    }
  };

  const handleAddReward = async () => {
    if (!newReward.title) return alert("Titre requis");
    try {
      const payload: any = {
        business_id: business.id,
        title: newReward.title,
        description: newReward.description,
        cost_points: newReward.cost,
      };

      const { error } = await supabase.from('rewards').insert(payload);
      if (error) throw error;

      alert("Récompense ajoutée !");
      setNewReward({ title: '', cost: 500, description: '' });
      fetchBusinessData();
    } catch (e: any) {
      alert("Erreur récompense : " + (e?.message || 'inconnue'));
    }
  };

  const handleCancelReservation = async () => {
    if (!selectedRes?.id) return;
    if (!confirm("Annuler cette réservation ?")) return;

    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status: 'cancelled' })
        .eq('id', selectedRes.id);

      if (error) throw error;

      alert("Réservation annulée.");
      setSelectedRes(null);
      fetchBusinessData();
    } catch (e: any) {
      alert("Erreur annulation : " + (e?.message || 'inconnue'));
    }
  };

  const handleValidateLoyalty = async () => {
    if (!loyaltyScan.id) return alert("ID Client requis.");
    setLoyaltyScan(prev => ({ ...prev, message: 'Validation en cours...' }));
    try {
      const { error } = await supabase.rpc('add_points', {
        p_user_id: loyaltyScan.id.trim(),
        p_amount: loyaltyScan.points,
        p_action: 'PURCHASE',
        p_desc: `Achat chez ${business.name}`
      });
      if (error) throw error;

      setLoyaltyScan({ id: '', points: 10, message: '✅ Points envoyés avec succès !' });
      setTimeout(() => setLoyaltyScan(prev => ({ ...prev, message: '' })), 3000);
    } catch (e: any) {
      setLoyaltyScan(prev => ({ ...prev, message: `❌ Erreur : ${e?.message || 'ID invalide ou introuvable.'}` }));
    }
  };

  const handleAiSmartSync = async () => {
    if (!confirm("Lancer l'analyse IA de vos calendriers externes (Airbnb/Booking) ?\n\nCela va bloquer les dates conflictuelles dans votre calendrier Forfeo.")) return;
    setAiLoading(prev => ({ ...prev, sync: true }));
    await new Promise(resolve => setTimeout(resolve, 1800));

    try {
      if (accommodations.length === 0) return alert("Erreur: Créez d'abord une chambre.");
      const targetRoomId = accommodations[0].id;
      const today = new Date();

      const b1Start = new Date(today); b1Start.setDate(today.getDate() + 7);
      const b1End = new Date(b1Start); b1End.setDate(b1Start.getDate() + 3);

      const b2Start = new Date(today); b2Start.setDate(today.getDate() + 20);
      const b2End = new Date(b2Start); b2End.setDate(b2Start.getDate() + 2);

      const { error } = await supabase.from('bookings').insert([
        {
          business_id: business.id, accommodation_id: targetRoomId, user_id: business.owner_id,
          check_in_date: b1Start.toISOString(), check_out_date: b1End.toISOString(),
          status: 'blocked', payment_status: 'none', total_price: 0,
          guest_name: 'Sync Airbnb (IA)', guest_email: 'ai@forfeo.com'
        },
        {
          business_id: business.id, accommodation_id: targetRoomId, user_id: business.owner_id,
          check_in_date: b2Start.toISOString(), check_out_date: b2End.toISOString(),
          status: 'blocked', payment_status: 'none', total_price: 0,
          guest_name: 'Sync Booking (IA)', guest_email: 'ai@forfeo.com'
        }
      ]);
      if (error) throw error;

      alert("✅ Synchronisation IA terminée : 2 conflits détectés et bloqués.");
      fetchBusinessData();
    } catch (e: any) {
      alert("Erreur Sync: " + (e?.message || 'inconnue'));
    } finally {
      setAiLoading(prev => ({ ...prev, sync: false }));
    }
  };

  const handleDelete = async (table: string, id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) return;
    await supabase.from(table).delete().eq('id', id);
    fetchBusinessData();
  };

  const handleLogout = async () => { await supabase.auth.signOut(); navigate('/'); };

  // Navigation Mobile
  const MobileNav = () => (
    <div className="md:hidden mb-6 border-b border-slate-100 pb-2 overflow-x-auto">
      <div className="flex gap-2 px-1 w-max">
        {MENU_GROUPS.flatMap(g => g.items).map(item => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all ${activeTab === item.id ? 'bg-slate-900 text-white shadow-md' : 'bg-white text-slate-500 border border-slate-200'}`}
          >
            {item.icon} {item.label}
            {(item as any).count ? <span className="bg-red-500 text-white text-[11px] px-1.5 py-0.5 rounded-full">{(item as any).count}</span> : null}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  if (!business) return null;

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col text-slate-800">
      <Header />

      {/* --- MODALE DÉTAILS RÉSERVATION --- */}
      {selectedRes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden relative">
            <button onClick={() => setSelectedRes(null)} className="absolute top-4 right-4 bg-slate-100 hover:bg-slate-200 p-2 rounded-full text-slate-500"><FaTimes size={16} /></button>
            <div className="bg-purple-600 p-6 text-white text-center">
              <h3 className="font-black text-xl uppercase tracking-wider">Détails Réservation</h3>
              <p className="text-purple-200 text-sm font-bold mt-1">ID: {shortId(selectedRes.id)}</p>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4 border-b border-slate-100 pb-4">
                <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg">
                  {selectedRes.guest_name ? selectedRes.guest_name.charAt(0) : 'U'}
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-lg">{selectedRes.guest_name || 'Client Inconnu'}</p>
                  <p className="text-slate-500 text-sm">{selectedRes.guest_email || 'Email non fourni'}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Arrivée</p>
                  <p className="font-bold text-slate-800">{selectedRes.start_at ? new Date(selectedRes.start_at).toLocaleDateString() : '-'}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Départ</p>
                  <p className="font-bold text-slate-800">{selectedRes.end_at ? new Date(selectedRes.end_at).toLocaleDateString() : '-'}</p>
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                <span className="font-bold text-slate-600">Total Payé</span>
                <span className="font-black text-green-600 text-xl">{toNum(selectedRes.total_price).toFixed(2)}$</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <button onClick={() => { setSelectedRes(null); setActiveTab('messages'); }} className="flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition"><FaPaperPlane /> Contacter</button>
                <button onClick={handleCancelReservation} className="flex items-center justify-center gap-2 bg-red-50 text-red-600 border border-red-100 py-3 rounded-xl font-bold hover:bg-red-100 transition"><FaBan /> Annuler</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto flex w-full max-w-[1600px] min-w-0 flex-grow gap-4 px-2.5 py-4 pt-20 sm:gap-6 sm:px-4 sm:py-8 sm:pt-24 lg:px-6">
        
        {/* SIDEBAR DESKTOP */}
        <aside className="hidden md:flex flex-col w-72 bg-white rounded-[2rem] shadow-xl border border-slate-100 overflow-hidden h-[calc(100vh-8rem)] sticky top-28">
          <div className="p-8 bg-slate-50/50 border-b border-slate-100 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 bg-grid-slate-200/50 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]"></div>
            <div className="w-24 h-24 bg-white rounded-2xl mx-auto mb-4 overflow-hidden border-4 border-white shadow-lg cursor-pointer group relative z-10" onClick={() => profileFileInputRef.current?.click()}>
              {formData.image_url ? <img src={formData.image_url} className="w-full h-full object-cover transition group-hover:scale-110" /> : <FaCamera className="w-full h-full p-6 text-slate-300 group-hover:text-slate-500 transition" />}
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-xs font-bold">Modifier</div>
            </div>
            <input type="file" ref={profileFileInputRef} onChange={e => handleUpload(e, 'profile')} className="hidden" />
            <h2 className="font-black text-xl truncate relative z-10">{formData.name || "Mon Entreprise"}</h2>
            {isPremium && <span className="inline-flex items-center gap-1 text-[10px] bg-gradient-to-r from-yellow-200 to-yellow-400 text-yellow-900 px-3 py-1 rounded-full font-black uppercase mt-2 shadow-sm relative z-10"><FaGem /> Partenaire Vérifié</span>}
            <p className="text-xs text-slate-500 font-bold uppercase mt-2 relative z-10">{formData.category} - {formData.city}</p>
          </div>

          <nav className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            {MENU_GROUPS.map((group, idx) => (
              <div key={idx} className="mb-6 last:mb-0">
                <h4 className="px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{group.title}</h4>
                <div className="space-y-1">
                  {group.items.map(item => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id as any)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all group ${activeTab === item.id ? 'bg-slate-900 text-white shadow-md' : (item as any).special ? 'text-blue-600 hover:bg-blue-50' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`text-lg ${activeTab === item.id ? 'text-white' : (item as any).special ? 'text-blue-500' : 'text-slate-400 group-hover:text-slate-600'}`}>{item.icon}</span>
                        <span className="tracking-tight">{item.label}</span>
                      </div>
                      {(item as any).count !== undefined && (item as any).count > 0 && <span className={`text-[10px] px-2 py-0.5 rounded-full font-black ${activeTab === item.id ? 'bg-white/20 text-white' : 'bg-red-500 text-white'}`}>{(item as any).count}</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="p-4 mt-auto border-t border-slate-100 bg-slate-50/50">
            <Link to={`/place/${business.id}`} target="_blank" className="w-full flex items-center justify-center gap-2 bg-white border-2 border-slate-900 text-slate-900 py-3 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-900 hover:text-white transition mb-3 shadow-sm"><FaExternalLinkAlt /> Voir ma fiche</Link>
            <button onClick={handleLogout} className="w-full text-red-500 text-xs font-bold flex items-center justify-center gap-2 hover:bg-red-50 py-2 rounded-xl transition"><FaSignOutAlt /> Déconnexion</button>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <MobileNav />

          {/* --- CONTENU --- */}
          
          {/* VUE D'ENSEMBLE */}
          {activeTab === 'overview' && (
            <div className="space-y-8 animate-in fade-in">
              <div className="bg-slate-900 p-5 md:p-10 rounded-[2.5rem] shadow-xl text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                <div className="relative z-10">
                  <h1 className="text-4xl font-black mb-2">Bonjour, {formData.name}.</h1>
                  <p className="text-slate-300 text-lg">Voici les performances de votre entreprise aujourd'hui.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <StatCard title="Vues Totales" value={isPremium ? stats.views : <FaLock size={20} />} icon={FaEye} color="blue" />
                <StatCard title="Revenu Total" value={`${toNum(stats.revenue).toFixed(2)}$`} icon={FaCoins} color="green" />
                <div onClick={() => setActiveTab('orders')} className="cursor-pointer hover:scale-105 transition"><StatCard title="Commandes (Payées)" value={orders.filter(o => o.status === 'paid').length} icon={FaShoppingBag} color="purple" /></div>
                <div onClick={() => setActiveTab('scanner')} className="cursor-pointer hover:scale-105 transition">
                  <StatCard title="Valider un Bon" value="Scanner" icon={FaQrcode} color="indigo" />
                </div>
              </div>
            </div>
          )}

          {/* SCANNER */}
          {activeTab === 'scanner' && (
            <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in">
              <div className="bg-white p-5 md:p-10 rounded-[2.5rem] shadow-lg border border-slate-100 text-center">
                <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaQrcode size={40} />
                </div>
                <h2 className="text-3xl font-black text-slate-900 mb-2">Valider un Bon d'Échange</h2>
                <p className="text-slate-500 mb-8">Entrez le code unique du client (ex: PICK-XXXX-XXXX) pour valider son forfait ou son offre flash.</p>
                
                <form onSubmit={handleValidateVoucher} className="max-w-md mx-auto relative">
                  <input 
                    type="text" 
                    placeholder="Code de réservation..." 
                    value={scanCode}
                    onChange={(e) => setScanCode(e.target.value.toUpperCase())}
                    className="w-full text-center p-6 text-2xl font-mono font-black uppercase tracking-[0.2em] rounded-2xl bg-slate-50 border-2 border-slate-200 focus:border-indigo-500 focus:bg-white transition outline-none"
                  />
                  <button 
                    type="submit"
                    disabled={scanLoading}
                    className="mt-6 w-full bg-indigo-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 hover:shadow-lg transition flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    {scanLoading ? <FaSync className="animate-spin" /> : <FaCheckCircle />}
                    {scanLoading ? 'Vérification...' : 'Valider ce code'}
                  </button>
                </form>

                {scanMessage && (
                  <div className={`mt-8 p-6 rounded-2xl border-2 flex items-start gap-4 text-left animate-in zoom-in duration-300 ${
                    scanMessage.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
                    scanMessage.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
                    'bg-yellow-50 border-yellow-200 text-yellow-800'
                  }`}>
                    <div className="pt-1">
                      {scanMessage.type === 'success' && <FaCheckCircle size={24} className="text-green-600" />}
                      {scanMessage.type === 'error' && <FaTimesCircle size={24} className="text-red-600" />}
                      {scanMessage.type === 'warning' && <FaExclamationTriangle size={24} className="text-yellow-600" />}
                    </div>
                    <div>
                      <h4 className="font-black text-lg mb-1">
                        {scanMessage.type === 'success' ? 'Bon Valide' : scanMessage.type === 'error' ? 'Échec de validation' : 'Attention'}
                      </h4>
                      <p className="font-bold text-sm opacity-90">{scanMessage.text}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
                <h3 className="font-black text-lg text-slate-900 mb-6 flex items-center gap-2">
                  <FaHistory className="text-slate-400" /> Historique des validations récentes
                </h3>
                <div className="space-y-3">
                  {orders.filter(o => o.status === 'picked_up' || o.fulfillment_status === 'fulfilled').slice(0, 5).map(order => (
                    <div key={order.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                      <div>
                        <p className="font-black text-slate-900">{order.customer_name || 'Client'}</p>
                        <p className="text-xs text-slate-500 font-medium">Code: <span className="font-mono text-indigo-600 font-bold">{order.pickup_code}</span></p>
                      </div>
                      <div className="text-right">
                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-[10px] font-black uppercase">Validé</span>
                        <p className="text-[10px] text-slate-400 mt-1 font-bold">{new Date(order.updated_at || order.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                  {orders.filter(o => o.status === 'picked_up' || o.fulfillment_status === 'fulfilled').length === 0 && (
                    <p className="text-center text-slate-400 font-medium py-4">Aucune validation récente.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* COMMANDES */}
          {activeTab === 'orders' && (
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in">
              <div className="bg-white p-5 md:p-10 rounded-[2.5rem] shadow-lg border border-slate-100">
                <h2 className="text-3xl font-black mb-6 flex items-center gap-3"><FaShoppingBag className="text-green-600" /> Historique des Commandes</h2>
                {orders.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-3xl">
                    <p className="text-slate-500 font-bold">Aucune commande enregistrée pour le moment.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-slate-100 text-xs text-slate-400 uppercase tracking-widest">
                          <th className="py-4 px-2">Référence</th>
                          <th className="py-4 px-2">Date</th>
                          <th className="py-4 px-2">Client</th>
                          <th className="py-4 px-2">Montant</th>
                          <th className="py-4 px-2 text-right">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm font-bold">
                        {orders.map(o => (
                          <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50 transition">
                            <td className="py-4 px-2 font-mono text-xs text-slate-500">{o.pickup_code || shortId(o.id)}</td>
                            <td className="py-4 px-2 text-slate-500 font-medium">{new Date(o.created_at).toLocaleDateString()}</td>
                            <td className="py-4 px-2 text-slate-900">{o.customer_name || o.customer_email || 'Anonyme'}</td>
                            <td className="py-4 px-2 text-green-600">{toNum(o.total_amount).toFixed(2)}$</td>
                            <td className="py-4 px-2 text-right">
                              <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                                o.status === 'paid' ? 'bg-blue-100 text-blue-700' :
                                o.status === 'picked_up' || o.fulfillment_status === 'fulfilled' ? 'bg-green-100 text-green-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {o.status === 'paid' ? 'Payé' : o.status === 'picked_up' || o.fulfillment_status === 'fulfilled' ? 'Validé' : o.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PORTEFEUILLE */}
          {activeTab === 'wallet' && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in">
              <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white p-5 md:p-10 rounded-[2.5rem] relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 p-8 opacity-10"><FaCoins size={120} /></div>
                <div className="relative z-10">
                  <h2 className="text-3xl font-black mb-2 flex items-center gap-3"><FaCoins className="text-yellow-400" /> Revenus & Versements</h2>
                  <p className="text-blue-200 mb-8">Gérez vos encaissements en temps réel via Stripe.</p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <div className="bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/20">
                      <p className="text-xs font-bold uppercase text-blue-300 mb-2">Revenu Total (Forfeo)</p>
                      <p className="text-4xl font-black text-white">{toNum(stats.revenue).toFixed(2)} $</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/20">
                      <p className="text-xs font-bold uppercase text-blue-300 mb-2">En cours de virement (Stripe)</p>
                      <p className="text-4xl font-black text-green-400 flex items-center gap-2"><FaSync className="animate-spin text-sm" /> ... $</p>
                      <p className="text-[10px] text-blue-200 mt-1">Dépend de votre calendrier de virement Stripe.</p>
                    </div>
                  </div>

                  {!business.stripe_account_id ? (
                    <div className="bg-white text-slate-900 p-8 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg">
                      <div>
                        <h3 className="font-black text-xl flex items-center gap-2 mb-2"><span className="text-[#635BFF]">Stripe</span> Connect</h3>
                        <p className="text-slate-600 leading-relaxed">Pour recevoir vos paiements, vous devez connecter un compte bancaire sécurisé.</p>
                      </div>
                      <button onClick={handleConnectStripe} disabled={loading} className="bg-[#635BFF] text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-[#534bd9] transition shadow-lg flex items-center gap-3 whitespace-nowrap disabled:opacity-50">
                        {loading ? "Chargement..." : "Configurer les virements"} <FaArrowRight />
                      </button>
                    </div>
                  ) : (
                    <div className="bg-green-500/20 border-2 border-green-500/50 p-6 rounded-3xl flex items-center gap-4 text-green-100 font-bold backdrop-blur-md">
                      <FaCheckCircle className="text-3xl" />
                      <div>
                        <p className="text-lg">Compte bancaire connecté et actif.</p>
                        <p className="text-sm text-green-200 font-normal">Vos virements sont gérés automatiquement par Stripe.</p>
                      </div>
                      <button className="ml-auto text-sm bg-white/10 px-6 py-3 rounded-2xl hover:bg-white/20 transition font-black uppercase tracking-widest">Gérer sur Stripe ↗</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* HÉBERGEMENT */}
          {activeTab === 'hotel' && (
            <div className="max-w-5xl mx-auto space-y-10 animate-in fade-in">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
                <div>
                  <h2 className="text-3xl font-black text-slate-900 mb-2 flex items-center gap-3"><FaBed className="text-blue-600" /> Mon Hébergement</h2>
                  <p className="text-slate-500">Gérez vos chambres, équipements et règles.</p>
                </div>
                <button onClick={() => setActiveTab('calendar')} className="bg-orange-100 text-orange-700 px-6 py-3 rounded-2xl font-black uppercase tracking-widest hover:bg-orange-200 transition flex items-center gap-2">
                  <FaCalendarCheck /> Gérer les Disponibilités
                </button>
              </div>

              {/* ✅ COMPOSANT RESTAURATION */}
              <BusinessDiningSection businessId={business?.id} />

              <div className="bg-white rounded-[2.5rem] shadow-lg border border-slate-100 overflow-hidden">
                <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                  <div>
                    <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                      <FaConciergeBell className="text-purple-600" /> Infos Établissement
                    </h3>
                    <p className="text-sm text-slate-500">Équipements, Règles et FAQ voyageurs.</p>
                  </div>
                  <button
                    onClick={handleSaveHotelDetails}
                    disabled={hotelDetailsLoading}
                    className="bg-slate-900 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition flex items-center gap-2 disabled:opacity-50"
                  >
                    {hotelDetailsLoading ? <FaSync className="animate-spin" /> : <FaSave />} Enregistrer
                  </button>
                </div>

                <div className="p-8 space-y-10">
                  {/* 1. ÉQUIPEMENTS */}
                  <div>
                    <h4 className="font-bold text-slate-900 uppercase tracking-widest text-xs mb-4 flex items-center gap-2">
                      <FaStar className="text-yellow-500" /> Équipements
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {Object.entries(HOTEL_AMENITIES_OPTIONS).map(([category, opts]) => (
                        <div key={category} className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                          <h5 className="font-bold text-sm text-slate-700 mb-3">{category}</h5>
                          <div className="space-y-2">
                            {opts.map((opt) => {
                              const isSelected = hotelDetails.amenities.includes(opt.label);
                              return (
                                <label key={opt.label} className="flex items-center gap-3 cursor-pointer group">
                                  <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition ${isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300'}`}>
                                    {isSelected && <FaCheck size={10} />}
                                  </div>
                                  <input
                                    type="checkbox"
                                    className="hidden"
                                    checked={isSelected}
                                    onChange={() => {
                                      const newAmenities = isSelected
                                        ? hotelDetails.amenities.filter(a => a !== opt.label)
                                        : [...hotelDetails.amenities, opt.label];
                                      setHotelDetails(prev => ({ ...prev, amenities: newAmenities }));
                                    }}
                                  />
                                  <span className={`text-sm font-medium transition ${isSelected ? 'text-blue-700' : 'text-slate-600 group-hover:text-slate-900'}`}>
                                    <span className="inline-flex items-center gap-2">{opt.icon} {opt.label}</span>
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 2. RÈGLES */}
                  <div className="border-t border-slate-100 pt-8">
                    <h4 className="font-bold text-slate-900 uppercase tracking-widest text-xs mb-4 flex items-center gap-2">
                      <FaGavel className="text-red-500" /> Règles de la maison
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <div className="flex gap-4">
                          <div className="flex-1">
                            <label className="text-xs font-bold text-slate-400 uppercase">Arrivée (Check-in)</label>
                            <input
                              type="time"
                              value={hotelDetails.rules.check_in}
                              onChange={e => setHotelDetails(prev => ({ ...prev, rules: { ...prev.rules, check_in: e.target.value } }))}
                              className="w-full p-3 bg-slate-50 rounded-xl border border-slate-200 font-bold"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs font-bold text-slate-400 uppercase">Départ (Check-out)</label>
                            <input
                              type="time"
                              value={hotelDetails.rules.check_out}
                              onChange={e => setHotelDetails(prev => ({ ...prev, rules: { ...prev.rules, check_out: e.target.value } }))}
                              className="w-full p-3 bg-slate-50 rounded-xl border border-slate-200 font-bold"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-slate-400 uppercase">Règles Spécifiques</label>
                          <textarea
                            placeholder="Ex: Pas de bruit après 22h, clés à remettre en main propre..."
                            className="w-full p-3 bg-slate-50 rounded-xl border border-slate-200 font-medium text-sm h-24 resize-none"
                            value={hotelDetails.rules.custom_rules || ''}
                            onChange={e => setHotelDetails(prev => ({ ...prev, rules: { ...prev.rules, custom_rules: e.target.value } }))}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        {[
                          { key: 'smoking_allowed', label: "Fumeur autorisé", icon: <FaSmokingBan /> },
                          { key: 'pets_allowed', label: "Animaux acceptés", icon: <FaDog /> },
                          { key: 'parties_allowed', label: "Fêtes / Événements autorisés", icon: <FaGlassCheers /> },
                        ].map((rule) => (
                          <div key={rule.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <span className="flex items-center gap-2 text-sm font-bold text-slate-700">{rule.icon} {rule.label}</span>
                            <label className="inline-flex items-center cursor-pointer select-none">
                              <span className="sr-only">{rule.label}</span>
                              <div className={`toggle ${!!(hotelDetails.rules as any)[rule.key] ? 'toggle-on' : ''}`}>
                                <span className="toggle-knob" />
                              </div>
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={!!(hotelDetails.rules as any)[rule.key]}
                                onChange={e => setHotelDetails(prev => ({ ...prev, rules: { ...prev.rules, [rule.key]: e.target.checked } }))}
                              />
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 3. FAQ */}
                  <div className="border-t border-slate-100 pt-8">
                    <h4 className="font-bold text-slate-900 uppercase tracking-widest text-xs mb-4 flex items-center gap-2">
                      <FaQuestionCircle className="text-blue-500" /> Questions Fréquentes (Q/R Voyageurs)
                    </h4>
                    <div className="space-y-4">
                      {hotelDetails.faq.map((item, idx) => (
                        <div key={idx} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 relative group">
                          <button onClick={() => setHotelDetails(prev => ({ ...prev, faq: prev.faq.filter((_, i) => i !== idx) }))} className="absolute top-2 right-2 text-red-400 hover:text-red-600 p-2"><FaTrash size={12} /></button>
                          <input
                            type="text"
                            placeholder="Question (ex: Y a-t-il un parking ?)"
                            className="w-full bg-transparent font-bold text-slate-900 mb-2 focus:outline-none placeholder:text-slate-400"
                            value={item.question}
                            onChange={e => {
                              const newFaq = [...hotelDetails.faq];
                              newFaq[idx].question = e.target.value;
                              setHotelDetails(prev => ({ ...prev, faq: newFaq }));
                            }}
                          />
                          <textarea
                            placeholder="Réponse..."
                            rows={2}
                            className="w-full bg-white p-2 rounded-xl border border-slate-200 text-sm focus:outline-none resize-none"
                            value={item.answer}
                            onChange={e => {
                              const newFaq = [...hotelDetails.faq];
                              newFaq[idx].answer = e.target.value;
                              setHotelDetails(prev => ({ ...prev, faq: newFaq }));
                            }}
                          />
                        </div>
                      ))}
                      <button
                        onClick={() => setHotelDetails(prev => ({ ...prev, faq: [...prev.faq, { question: '', answer: '' }] }))}
                        className="w-full py-3 border-2 border-dashed border-slate-300 rounded-2xl text-slate-500 font-bold hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300 transition flex items-center justify-center gap-2 text-sm"
                      >
                        <FaPlus /> Ajouter une Question/Réponse
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ====== AJOUT CHAMBRE ====== */}
              <div className="bg-white p-5 md:p-10 rounded-[2.5rem] shadow-lg border border-slate-100 relative overflow-hidden">
                <h3 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-2 relative z-10"><FaPlus className="text-blue-600" /> Ajouter une chambre</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Nom de la chambre</label>
                        <input type="text" placeholder="ex: Suite Deluxe Vue Mer" value={newRoom.name} onChange={e => setNewRoom({ ...newRoom, name: e.target.value })} className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-blue-500 focus:bg-white transition font-bold" />
                      </div>
                      <div className="w-1/3">
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Capacité</label>
                        <input type="number" min="1" value={newRoom.capacity_adults} onChange={e => setNewRoom({ ...newRoom, capacity_adults: parseInt(e.target.value, 10) })} className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-blue-500 focus:bg-white transition font-bold text-center" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Prix par nuit ($)</label>
                      <div className="relative">
                        <span className="absolute left-4 top-4 text-slate-400 font-bold">$</span>
                        <input type="number" placeholder="0.00" value={newRoom.price_per_night} onChange={e => setNewRoom({ ...newRoom, price_per_night: e.target.value })} className="w-full p-4 pl-8 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-blue-500 focus:bg-white transition font-bold text-xl" />
                      </div>
                    </div>

                    <div className="relative">
                      <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Description</label>
                      <textarea rows={5} placeholder="Décrivez les atouts de cette chambre..." value={newRoom.description} onChange={e => setNewRoom({ ...newRoom, description: e.target.value })} className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-slate-100 focus:border-blue-500 focus:bg-white transition font-medium resize-none"></textarea>
                      <AiMagicButton onClick={() => simulateAiGeneration('description', 'hotel')} loading={aiLoading.hotel} label="Générer avec IA" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Galerie Photos (Max 10)</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                      {(newRoom.images || []).map((img, i) => (
                        <div key={i} className="aspect-square rounded-2xl overflow-hidden relative group shadow-sm border border-slate-200">
                          <img src={img} className="w-full h-full object-cover" />
                          <button onClick={() => setNewRoom(prev => ({ ...prev, images: (prev.images || []).filter((_, idx) => idx !== i) }))} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition"><FaTimes size={10} /></button>
                        </div>
                      ))}
                      {(newRoom.images || []).length < 10 && (
                        <div onClick={() => roomFileInputRef.current?.click()} className="aspect-square rounded-2xl bg-slate-50 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:bg-slate-100 hover:border-blue-400 hover:text-blue-500 transition group">
                          {uploading ? <FaSync className="animate-spin mb-2" /> : <FaCamera className="text-2xl mb-2 group-hover:scale-110 transition" />}
                          <span className="text-[10px] font-bold uppercase tracking-widest">{uploading ? 'Upload...' : 'Ajouter'}</span>
                        </div>
                      )}
                    </div>
                    <input type="file" ref={roomFileInputRef} onChange={e => handleUpload(e, 'room')} className="hidden" multiple accept="image/*" />
                    <p className="text-xs text-slate-500">Ajoutez au moins 3 photos de haute qualité pour une meilleure conversion.</p>
                  </div>
                </div>

                <button onClick={handleAddRoom} className="mt-8 w-full bg-blue-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-blue-700 transition shadow-lg flex items-center justify-center gap-3 relative z-10">
                  <FaPlus /> Créer la chambre
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {accommodations.map(room => (
                  <div key={room.id} className="bg-white rounded-[2rem] overflow-hidden shadow-md border border-slate-100 group hover:shadow-xl transition-all">
                    <div className="h-48 relative">
                      <img src={getRoomCover(room)} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-black uppercase flex items-center gap-1 shadow-sm text-slate-900">
                        <FaUsers className="text-blue-500" /> {toNum(room.capacity_adults, 1)} Pers.
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="text-xl font-black text-slate-900 leading-tight">{room.name}</h4>
                        <p className="text-xl font-black text-blue-600">{toNum(room.price_per_night).toFixed(2)}$<span className="text-xs text-slate-400 font-bold">/nuit</span></p>
                      </div>
                      <p className="text-slate-500 text-sm line-clamp-2 mb-6">{room.description}</p>
                      <div className="flex gap-2 pt-4 border-t border-slate-50">
                        <button onClick={() => setActiveTab('calendar')} className="flex-1 bg-slate-100 text-slate-700 py-3 rounded-xl font-bold text-sm uppercase tracking-widest hover:bg-slate-200 transition flex items-center justify-center gap-2"><FaCalendarAlt /> Disponibilités</button>
                        <button onClick={() => handleDelete('accommodations', room.id)} className="bg-red-50 text-red-500 p-3 rounded-xl hover:bg-red-100 transition"><FaTrash /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* HÔTEL MYSTÈRE */}
              <div className="bg-white p-5 md:p-10 rounded-[2.5rem] shadow-lg border border-slate-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-5 md:p-10 opacity-5 text-slate-900">
                  <FaHotel size={160} />
                </div>

                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
                  <div>
                    <h3 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-slate-900 text-white"><FaEyeSlash /></span>
                      Hôtel Mystère
                    </h3>
                    <p className="text-slate-500 mt-1">L'image publique est floutée (style Hotwire). L'image réelle est conservée en "secret".</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-400">
                    <FaInfoCircle /> Offres actives: <span className="text-slate-900">{mysteryOffers.length}</span>
                  </div>
                </div>

                {/* Création */}
                <div className="bg-slate-50 border border-slate-100 rounded-[2rem] p-6 md:p-8">
                  <h4 className="font-black text-slate-900 mb-6 flex items-center gap-2"><FaPlus className="text-indigo-600" /> Créer une offre Hôtel Mystère</h4>
                  <div className="flex flex-col md:flex-row gap-8">
                    <div className="w-full md:w-1/3 space-y-4">
                      <div onClick={() => mysteryFileInputRef.current?.click()} className="aspect-[4/5] rounded-3xl bg-white border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:bg-slate-50 hover:border-indigo-400 hover:text-indigo-500 transition group relative overflow-hidden">
                        {(() => {
                          const showUrl = (mysteryPreviewMode === 'secret' && newMysteryOffer.secret_image_url) ? newMysteryOffer.secret_image_url : newMysteryOffer.image_url;
                          if (showUrl) {
                            const shouldBlurPreview = mysteryPreviewMode === 'secret' && newMysteryOffer.blur_enabled;
                            return <img src={showUrl} className="w-full h-full object-cover" style={shouldBlurPreview ? { filter: `blur(${toNum(newMysteryOffer.blur_radius, 16)}px)` } : undefined} />;
                          }
                          return <><FaCamera className="text-4xl mb-4 group-hover:scale-110 transition" /><span className="text-xs font-bold uppercase tracking-widest">{uploading ? 'Upload...' : 'Ajouter Photo'}</span></>;
                        })()}
                      </div>
                      <input type="file" ref={mysteryFileInputRef} onChange={e => handleUpload(e, 'mystery')} className="hidden" accept="image/*" />

                      <div className="bg-white border border-slate-200 rounded-2xl p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-black uppercase tracking-widest text-slate-500">Préview</p>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setMysteryPreviewMode('public')} className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${mysteryPreviewMode === 'public' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>Publique</button>
                            <button type="button" onClick={() => setMysteryPreviewMode('secret')} className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${mysteryPreviewMode === 'secret' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>Réelle</button>
                          </div>
                        </div>

                        <div className="mt-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-600 flex items-center gap-2"><FaEyeSlash className="text-indigo-600" /> Flouter l'image publique</label>
                            <input type="checkbox" checked={!!newMysteryOffer.blur_enabled} onChange={(e) => setNewMysteryOffer((p: any) => ({ ...p, blur_enabled: e.target.checked }))} />
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Intensité</span>
                            <input type="range" min={6} max={28} value={toNum(newMysteryOffer.blur_radius, 16)} onChange={(e) => setNewMysteryOffer((p: any) => ({ ...p, blur_radius: parseInt(e.target.value) }))} className="w-full" disabled={!newMysteryOffer.blur_enabled} />
                            <span className="text-xs font-black text-slate-700 w-10 text-right">{toNum(newMysteryOffer.blur_radius, 16)}px</span>
                          </div>
                          <button type="button" onClick={generateMysteryBlurredPublicImage} disabled={mysteryImgProcessing || !newMysteryOffer.blur_enabled} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-black uppercase tracking-widest hover:bg-indigo-700 transition flex items-center justify-center gap-2 disabled:opacity-50">
                            {mysteryImgProcessing ? <FaSync className="animate-spin" /> : <FaEyeSlash />}
                            {mysteryImgProcessing ? 'Génération...' : 'Générer flou'}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 space-y-6">
                      <div>
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Titre</label>
                        <input type="text" placeholder="ex: Escapade Mystère 1 nuit" value={newMysteryOffer.title} onChange={e => setNewMysteryOffer({ ...newMysteryOffer, title: e.target.value })} className="w-full p-4 rounded-2xl bg-white border-2 border-slate-200 focus:border-indigo-500 focus:bg-white transition font-bold text-lg" />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Prix Mystère ($)</label>
                          <input type="number" placeholder="149" value={newMysteryOffer.mystery_price} onChange={e => setNewMysteryOffer({ ...newMysteryOffer, mystery_price: e.target.value })} className="w-full p-4 rounded-2xl bg-indigo-50 border-2 border-indigo-100 focus:border-indigo-500 focus:bg-white transition font-black text-indigo-700 text-xl" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Prix Original</label>
                          <input type="number" placeholder="249" value={newMysteryOffer.original_price} onChange={e => setNewMysteryOffer({ ...newMysteryOffer, original_price: e.target.value })} className="w-full p-4 rounded-2xl bg-white border-2 border-slate-200 focus:border-indigo-500 focus:bg-white transition font-bold" />
                        </div>
                        <div>
                          <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Qté</label>
                          <input type="number" min={1} value={newMysteryOffer.quantity_available} onChange={e => setNewMysteryOffer({ ...newMysteryOffer, quantity_available: parseInt(e.target.value || '1') })} className="w-full p-4 rounded-2xl bg-white border-2 border-slate-200 focus:border-indigo-500 font-bold text-center" />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Expire (h)</label>
                          <input type="number" min={1} value={newMysteryOffer.expires_in_hours} onChange={e => setNewMysteryOffer({ ...newMysteryOffer, expires_in_hours: parseInt(e.target.value || '72') })} className="w-full p-4 rounded-2xl bg-white border-2 border-slate-200 focus:border-indigo-500 font-bold text-center md:text-left" />
                        </div>
                      </div>

                      <div className="relative">
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-1">Description</label>
                        <textarea rows={4} placeholder="Décris l'expérience (sans révéler l'identité)." value={newMysteryOffer.description} onChange={e => setNewMysteryOffer({ ...newMysteryOffer, description: e.target.value })} className="w-full p-4 rounded-2xl bg-white border-2 border-slate-200 focus:border-indigo-500 focus:bg-white transition font-medium resize-none" />
                        <AiMagicButton onClick={() => simulateAiGeneration('description', 'mystery')} loading={aiLoading.mystery} label="IA Générer" />
                      </div>

                      <button onClick={handleCreateMysteryOffer} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:shadow-lg transition flex items-center justify-center gap-3"><FaEyeSlash /> Publier l'offre Mystère</button>
                    </div>
                  </div>
                </div>

                {/* Liste */}
                <div className="mt-10">
                  <h4 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2"><FaListAlt className="text-slate-600" /> Vos offres Hôtel Mystère</h4>
                  {mysteryOffers.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200"><FaEyeSlash className="mx-auto text-slate-300 mb-4" size={36} /><p className="text-slate-500 font-bold">Aucune offre Mystère.</p></div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {mysteryOffers.map((o: any) => {
                        const showReal = !!mysteryAdminShowRealById[o.id];
                        const imgUrl = (!!o?.secret_image_url && showReal) ? o.secret_image_url : (o?.image_url || formData.image_url || "https://placehold.co/800x500?text=Hotel+Mystere");
                        const shouldBlurCss = !showReal && (!o?.image_url || o?.image_url === o?.secret_image_url);
                        return (
                          <div key={o.id} className="bg-white rounded-[2rem] overflow-hidden shadow-md border border-slate-100 hover:shadow-xl transition-all">
                            <div className="h-44 relative">
                              <img src={imgUrl} className="w-full h-full object-cover" style={shouldBlurCss ? { filter: 'blur(18px)' } : undefined} />
                              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-900 flex items-center gap-2 shadow-sm"><FaHotel className="text-indigo-600" /> Offre Mystère</div>
                              {!!o.secret_image_url && (
                                <button
                                  onClick={() => setMysteryAdminShowRealById((p) => ({ ...p, [o.id]: !p[o.id] }))}
                                  className="absolute bottom-4 right-4 bg-slate-900/90 text-white px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm hover:bg-slate-900 transition"
                                >
                                  {showReal ? <FaEyeSlash /> : <FaEye />} {showReal ? 'Masquer' : 'Afficher'} réel
                                </button>
                              )}
                            </div>
                            <div className="p-6">
                              <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0"><h5 className="text-xl font-black text-slate-900 truncate">{o?.title || 'Offre Mystère'}</h5></div>
                                <div className="text-right"><div className="text-2xl font-black text-indigo-700">{toNum(o.mystery_price).toFixed(0)}$</div></div>
                              </div>
                              <div className="mt-4">
                                <button onClick={() => setExpandedMysteryId(prev => (prev === o.id ? null : o.id))} className="w-full bg-slate-100 text-slate-800 py-3 rounded-xl font-black uppercase tracking-widest hover:bg-slate-200 transition flex items-center justify-center gap-2 text-xs">
                                  <FaEye /> {expandedMysteryId === o.id ? 'Masquer' : 'Voir'} détails
                                </button>
                              </div>
                              {expandedMysteryId === o.id && <div className="mt-4 bg-slate-50 border border-slate-100 p-4 rounded-2xl text-sm text-slate-700"><p>{o?.description || '—'}</p></div>}
                              <div className="flex gap-2 pt-4 mt-4 border-t border-slate-50">
                                <button onClick={() => handleDelete('mystery_hotel_offers', o.id)} className="flex-1 bg-red-50 text-red-600 py-3 rounded-xl font-black uppercase tracking-widest hover:bg-red-100 transition flex items-center justify-center gap-2 text-xs"><FaTrash /> Supprimer</button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* --- OFFRES FLASH --- */}
          {activeTab === 'lastcall' && (
            <div className="max-w-5xl mx-auto bg-white p-5 md:p-10 rounded-[2.5rem] shadow-lg border border-slate-100 animate-in fade-in">
              <h2 className="text-3xl font-black mb-6 flex items-center gap-3"><FaFire className="text-red-500" /> Offres de Dernière Minute</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100">
                  <h3 className="font-black text-xl mb-6 text-slate-800">Créer une offre Flash</h3>
                  <div className="space-y-4">
                    <input type="text" placeholder="Titre (ex: -50% Ce soir)" value={newDeal.title} onChange={e => setNewDeal({ ...newDeal, title: e.target.value })} className="w-full p-4 rounded-2xl border-2 border-slate-200 font-bold" />
                    <div className="flex gap-4">
                      <input type="number" placeholder="Prix Origine" value={newDeal.original_price} onChange={e => setNewDeal({ ...newDeal, original_price: e.target.value })} className="w-full p-4 rounded-2xl border-2 border-slate-200 font-bold" />
                      <input type="number" placeholder="Prix Promo" value={newDeal.discount_price} onChange={e => setNewDeal({ ...newDeal, discount_price: e.target.value })} className="w-full p-4 rounded-2xl border-2 border-red-200 text-red-600 font-black" />
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1"><label className="text-[10px] font-black uppercase text-slate-400">Qté</label><input type="number" value={newDeal.quantity_available} onChange={e => setNewDeal({ ...newDeal, quantity_available: parseInt(e.target.value) })} className="w-full p-4 rounded-2xl border-2 border-slate-200 font-bold" /></div>
                      <div className="flex-1"><label className="text-[10px] font-black uppercase text-slate-400">Durée (h)</label><input type="number" value={newDeal.expires_in_hours} onChange={e => setNewDeal({ ...newDeal, expires_in_hours: parseInt(e.target.value) })} className="w-full p-4 rounded-2xl border-2 border-slate-200 font-bold" /></div>
                    </div>
                    <div className="relative">
                      <textarea placeholder="Description..." value={newDeal.description} onChange={e => setNewDeal({ ...newDeal, description: e.target.value })} className="w-full p-4 rounded-2xl border-2 border-slate-200 font-medium h-24 resize-none"></textarea>
                      <AiMagicButton onClick={() => simulateAiGeneration('description', 'deal')} loading={aiLoading.deal} />
                    </div>
                    <div className="flex items-center gap-4">
                      <div onClick={() => dealFileInputRef.current?.click()} className="w-16 h-16 bg-white border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-blue-500 text-slate-400 hover:text-blue-500 transition">
                        {newDeal.image_url ? <img src={newDeal.image_url} className="w-full h-full object-cover rounded-xl" /> : <FaCamera />}
                      </div>
                      <input type="file" ref={dealFileInputRef} onChange={e => handleUpload(e, 'deal')} className="hidden" />
                      <button 
                        onClick={handleCreateDeal} 
                        disabled={saving}
                        className="flex-1 bg-red-500 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-red-600 transition shadow-lg shadow-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? 'Publication...' : "Publier l'offre"}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {deals.map(deal => (
                    <div key={deal.id} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex gap-4">
                      <img src={deal.image_url || "https://via.placeholder.com/100"} className="w-20 h-20 rounded-xl object-cover bg-slate-100" />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <h4 className="font-black truncate">{deal.title}</h4>
                          <span className="text-red-500 font-black">{deal.discount_price}$</span>
                        </div>
                        <p className="text-xs text-slate-500 line-through">{deal.original_price}$</p>
                        <div className="mt-2 flex items-center justify-between text-xs font-bold text-slate-400">
                          <span className="flex items-center gap-1"><FaClock /> {new Date(deal.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded-md">{deal.quantity_available} dispo</span>
                        </div>
                      </div>
                      <button onClick={() => handleDelete('last_call_deals', deal.id)} className="text-slate-300 hover:text-red-500"><FaTrash /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* BOUTIQUE */}
          {activeTab === 'shop' && (
            <div className="max-w-5xl mx-auto bg-white p-5 md:p-10 rounded-[2.5rem] shadow-lg border border-slate-100 animate-in fade-in">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-black flex items-center gap-3"><FaBoxOpen className="text-blue-500" /> Produits Physiques</h2>
                <div className="bg-blue-50 text-blue-600 px-4 py-2 rounded-xl font-bold text-sm">🚚 Livraison requise</div>
              </div>
              <div className="bg-slate-50 p-6 rounded-[2rem] mb-8 border border-slate-100">
                <div className="flex gap-4 items-start">
                  <div onClick={() => productFileInputRef.current?.click()} className="w-24 h-24 bg-white border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 text-slate-400 hover:text-blue-500 transition shrink-0">
                    {newProduct.image_url ? <img src={newProduct.image_url} className="w-full h-full object-cover rounded-2xl" /> : <><FaCamera size={24} /><span className="text-[10px] font-black uppercase mt-1">Photo</span></>}
                  </div>
                  <input type="file" ref={productFileInputRef} onChange={e => handleUpload(e, 'product')} className="hidden" />
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input type="text" placeholder="Nom du produit" value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} className="w-full p-3 rounded-xl border border-slate-200 font-bold" />
                    <input type="number" placeholder="Prix ($)" value={newProduct.price} onChange={e => setNewProduct({ ...newProduct, price: e.target.value })} className="w-full p-3 rounded-xl border border-slate-200 font-bold" />
                    <div className="col-span-2 relative">
                      <input type="text" placeholder="Description courte..." value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} className="w-full p-3 rounded-xl border border-slate-200 font-medium pr-10" />
                      <button onClick={() => simulateAiGeneration('description', 'product')} className="absolute right-2 top-2 text-purple-600 hover:text-purple-800"><FaMagic /></button>
                    </div>
                  </div>
                  <button onClick={handleAddProduct} className="h-24 px-6 bg-blue-600 text-white rounded-2xl font-black uppercase hover:bg-blue-700 transition shadow-lg flex flex-col items-center justify-center gap-1"><FaPlus size={20} /><span>Ajouter</span></button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {products.map(p => (
                  <div key={p.id} className="bg-white border border-slate-100 rounded-2xl overflow-hidden group hover:shadow-lg transition">
                    <div className="h-32 bg-slate-100 relative">
                      <img src={p.image_url || "https://via.placeholder.com/150"} className="w-full h-full object-cover" />
                      <button onClick={() => handleDelete('shop_products', p.id)} className="absolute top-2 right-2 bg-white/80 p-1.5 rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition"><FaTrash size={12} /></button>
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-sm truncate">{p.name}</h4>
                      <p className="text-blue-600 font-black">{p.price}$</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PUBLICATIONS */}
          {activeTab === 'posts' && (
            <div className="max-w-3xl mx-auto bg-white p-8 rounded-[2.5rem] shadow-lg border border-slate-100 animate-in fade-in">
              <h2 className="text-2xl font-black mb-6 flex items-center gap-2"><FaBullhorn className="text-pink-500" /> Fil d'actualité</h2>
              <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 mb-8">
                <textarea className="w-full bg-transparent border-none focus:ring-0 text-lg font-medium placeholder:text-slate-400 resize-none" rows={3} placeholder="Quoi de neuf aujourd'hui ?" value={newPost.content} onChange={e => setNewPost({ ...newPost, content: e.target.value })}></textarea>
                {newPost.image_url && <div className="mt-4 relative"><img src={newPost.image_url} className="w-full h-48 object-cover rounded-2xl" /><button onClick={() => setNewPost({ ...newPost, image_url: '' })} className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-full"><FaTimes /></button></div>}
                <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
                  <div className="flex gap-4">
                    <div onClick={() => postFileInputRef.current?.click()} className="flex items-center gap-2 text-slate-500 hover:text-blue-600 cursor-pointer text-sm font-bold transition"><FaCamera /> Photo</div>
                    <button onClick={() => simulateAiGeneration('content', 'post')} className="flex items-center gap-2 text-purple-500 hover:text-purple-700 text-sm font-bold transition"><FaMagic /> IA</button>
                    <input type="file" ref={postFileInputRef} onChange={e => handleUpload(e, 'post')} className="hidden" />
                  </div>
                  <button onClick={handlePublishPost} disabled={isPosting} className="bg-slate-900 text-white px-6 py-2 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-slate-800 transition disabled:opacity-50">{isPosting ? '...' : 'Publier'}</button>
                </div>
              </div>
              <div className="space-y-6">
                {posts.map(post => (
                  <div key={post.id} className="border-b border-slate-100 pb-6 last:border-0">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full overflow-hidden"><img src={formData.image_url || "https://via.placeholder.com/40"} className="w-full h-full object-cover" /></div>
                        <div><p className="font-bold text-sm text-slate-900">{formData.name}</p><p className="text-[10px] text-slate-400 uppercase font-bold">{new Date(post.created_at).toLocaleDateString()}</p></div>
                      </div>
                      <button onClick={() => handleDelete('posts', post.id)} className="text-slate-300 hover:text-red-500"><FaTrash /></button>
                    </div>
                    <p className="text-slate-700 leading-relaxed mb-3">{post.content}</p>
                    {post.image_url && <img src={post.image_url} className="w-full rounded-2xl shadow-sm mb-3" />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FIDÉLITÉ */}
          {activeTab === 'loyalty' && (
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in">
              <div className="bg-white p-8 rounded-[2.5rem] shadow-lg border border-slate-100">
                <h2 className="text-2xl font-black mb-6 flex items-center gap-2"><FaQrcode className="text-slate-900" /> Scanner Client</h2>
                <div className="bg-slate-900 text-white p-8 rounded-3xl text-center mb-6">
                  <FaQrcode size={64} className="mx-auto mb-4 opacity-50" />
                  <p className="font-bold mb-4">Scanner le QR Code du client pour valider des points.</p>
                  <input type="text" placeholder="ID Client Manuel" value={loyaltyScan.id} onChange={e => setLoyaltyScan({ ...loyaltyScan, id: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl p-3 text-center text-white placeholder:text-slate-500 font-mono mb-3" />
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <button onClick={() => setLoyaltyScan(prev => ({ ...prev, points: Math.max(5, prev.points - 5) }))} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center font-bold">-</button>
                    <span className="text-2xl font-black text-yellow-400">{loyaltyScan.points} pts</span>
                    <button onClick={() => setLoyaltyScan(prev => ({ ...prev, points: prev.points + 5 }))} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center font-bold">+</button>
                  </div>
                  <button onClick={handleValidateLoyalty} className="w-full bg-white text-slate-900 py-3 rounded-xl font-black uppercase tracking-widest hover:bg-slate-100 transition">Valider l'achat</button>
                  {loyaltyScan.message && <p className="mt-3 text-sm font-bold text-yellow-300">{loyaltyScan.message}</p>}
                </div>
              </div>
              <div className="bg-white p-8 rounded-[2.5rem] shadow-lg border border-slate-100">
                <h2 className="text-2xl font-black mb-6 flex items-center gap-2"><FaGift className="text-pink-500" /> Récompenses</h2>
                <div className="space-y-4 mb-6">
                  {rewards.map(r => (
                    <div key={r.id} className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl border border-slate-100">
                      <div><p className="font-bold text-slate-900">{r.title}</p><p className="text-xs text-slate-500">{r.cost_points} points</p></div>
                      <button onClick={() => handleDelete('rewards', r.id)} className="text-slate-300 hover:text-red-500"><FaTrash /></button>
                    </div>
                  ))}
                </div>
                <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                  <h3 className="font-bold text-sm uppercase text-slate-400 mb-4">Nouvelle récompense</h3>
                  <div className="space-y-3">
                    <input type="text" placeholder="Titre (ex: Café gratuit)" value={newReward.title} onChange={e => setNewReward({ ...newReward, title: e.target.value })} className="w-full p-3 rounded-xl border border-slate-200 text-sm font-bold" />
                    <input type="number" placeholder="Coût en points" value={newReward.cost} onChange={e => setNewReward({ ...newReward, cost: parseInt(e.target.value) })} className="w-full p-3 rounded-xl border border-slate-200 text-sm font-bold" />
                    <button onClick={handleAddReward} className="w-full bg-pink-500 text-white py-3 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-pink-600 transition">Ajouter</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STATISTIQUES GLASSMORPHISM & NEON */}
          {activeTab === 'stats' && (
            <div className="max-w-5xl mx-auto animate-in fade-in">
              <div className="bg-slate-900 rounded-[2.5rem] p-5 md:p-10 shadow-2xl relative overflow-hidden">
                <div className="absolute top-[-50%] left-[-10%] w-96 h-96 bg-purple-600 rounded-full mix-blend-screen filter blur-[128px] opacity-40 animate-blob"></div>
                <div className="absolute top-[-20%] right-[-10%] w-96 h-96 bg-cyan-400 rounded-full mix-blend-screen filter blur-[128px] opacity-40 animate-blob animation-delay-2000"></div>
                
                <div className="relative z-10 text-white">
                  <h2 className="text-4xl font-black mb-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-purple-400">Performances Globales</h2>
                  <p className="text-slate-400 font-medium mb-10">Données en temps réel de l'activité sur votre fiche Forfeo.</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-3xl">
                      <div className="text-cyan-400 mb-2"><FaEye size={24} /></div>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Vues de la Fiche</p>
                      <p className="text-4xl font-black">{stats.views}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-3xl">
                      <div className="text-purple-400 mb-2"><FaMousePointer size={24} /></div>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Interactions (Clics)</p>
                      <p className="text-4xl font-black">{stats.clicks}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-3xl">
                      <div className="text-green-400 mb-2"><FaCoins size={24} /></div>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Chiffre d'Affaires</p>
                      <p className="text-4xl font-black">{stats.revenue.toFixed(2)}$</p>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2"><FaChartBar className="text-cyan-400" /> Évolution (7 derniers jours)</h3>
                  
                  <div className="h-72 w-full flex items-end justify-between gap-2 md:gap-4 relative">
                    <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-10">
                       <div className="w-full border-b border-white border-dashed h-0"></div>
                       <div className="w-full border-b border-white border-dashed h-0"></div>
                       <div className="w-full border-b border-white border-dashed h-0"></div>
                       <div className="w-full border-b border-white border-dashed h-0"></div>
                    </div>
                    {[40, 60, 35, 70, 50, 80, 95].map((h, i) => (
                      <div key={i} className="w-full relative group h-full flex items-end z-10">
                        <div 
                          className="w-full rounded-2xl bg-gradient-to-t from-purple-600/30 to-cyan-400/90 hover:to-cyan-300 transition-all duration-500 relative shadow-[0_0_15px_rgba(34,211,238,0.1)] group-hover:shadow-[0_0_25px_rgba(34,211,238,0.5)] cursor-pointer" 
                          style={{ height: `${h}%` }}
                        >
                           <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-white text-slate-900 text-xs font-black py-1.5 px-3 rounded-xl opacity-0 group-hover:opacity-100 transition-all shadow-xl transform translate-y-2 group-hover:translate-y-0">
                             {h * 12}
                           </div>
                        </div>
                        <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-bold text-slate-500 uppercase">J-{6-i}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PROFIL */}
          {activeTab === 'profile' && (
            <div className="max-w-3xl mx-auto bg-white p-5 md:p-10 rounded-[2.5rem] shadow-lg border border-slate-100 animate-in fade-in">
              <h2 className="text-3xl font-black mb-8 flex items-center gap-3"><FaUserEdit className="text-slate-900" /> Profil & Paramètres</h2>
              <form onSubmit={handleSaveProfile} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><label className="label">Nom de l'entreprise</label><input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="input-field" /></div>
                  <div><label className="label">Catégorie</label><select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className="input-field">{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                  <div><label className="label">Téléphone</label><input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="input-field" /></div>
                  <div><label className="label">Ville</label><input type="text" value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} className="input-field" /></div>
                </div>
                <div><label className="label">Description</label><textarea rows={4} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="input-field resize-none"></textarea></div>
                <div className="pt-6 border-t border-slate-100">
                  <h3 className="font-bold text-lg mb-4">Réseaux Sociaux</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative"><FaFacebook className="absolute top-4 left-4 text-slate-400" /><input type="text" placeholder="Facebook URL" value={formData.facebook_url} onChange={e => setFormData({ ...formData, facebook_url: e.target.value })} className="input-field pl-10" /></div>
                    <div className="relative"><FaInstagram className="absolute top-4 left-4 text-slate-400" /><input type="text" placeholder="Instagram URL" value={formData.instagram_url} onChange={e => setFormData({ ...formData, instagram_url: e.target.value })} className="input-field pl-10" /></div>
                    <div className="relative"><FaTiktok className="absolute top-4 left-4 text-slate-400" /><input type="text" placeholder="TikTok URL" value={formData.tiktok_url} onChange={e => setFormData({ ...formData, tiktok_url: e.target.value })} className="input-field pl-10" /></div>
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <button type="submit" disabled={saving} className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-slate-800 transition shadow-lg flex items-center gap-2 disabled:opacity-50">{saving ? <FaSync className="animate-spin" /> : <FaSave />} Enregistrer</button>
                </div>
              </form>

              <div className="mt-12 pt-8 border-t border-slate-100">
                <h3 className="text-xl font-black mb-6 flex items-center gap-2 text-slate-900"><FaLock className="text-slate-400" /> Sécurité</h3>
                <form onSubmit={handleUpdatePassword} className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    <div>
                      <label className="label">Nouveau mot de passe</label>
                      <input type="password" value={newPassword.new} onChange={e => setNewPassword({ ...newPassword, new: e.target.value })} className="input-field" placeholder="••••••" />
                    </div>
                    <div>
                      <label className="label">Confirmer</label>
                      <input type="password" value={newPassword.confirm} onChange={e => setNewPassword({ ...newPassword, confirm: e.target.value })} className="input-field" placeholder="••••••" />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button type="submit" disabled={saving || !newPassword.new} className="bg-white border-2 border-slate-200 text-slate-900 px-6 py-3 rounded-xl font-bold uppercase text-xs tracking-widest hover:bg-slate-900 hover:text-white hover:border-slate-900 transition disabled:opacity-50">Mettre à jour</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* CALENDRIER & MESSAGES */}
          {activeTab === 'calendar' && (
            <div className="max-w-5xl mx-auto bg-white p-8 rounded-[2.5rem] shadow-lg border border-slate-100 animate-in fade-in">
              <AvailabilityCalendar businessId={business.id} onUpdate={fetchBusinessData} onAiSync={handleAiSmartSync} aiLoading={aiLoading.sync} />
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="mx-auto h-[calc(100dvh-8.25rem)] min-h-0 w-full min-w-0 max-w-6xl overflow-hidden rounded-none border-0 bg-white p-0 shadow-none animate-in fade-in lg:h-[760px] lg:rounded-[2.5rem] lg:border lg:border-slate-100 lg:p-5 lg:shadow-lg">
              <ChatSystem userType="business" userId={business.owner_id} />
            </div>
          )}

          {/* RÉSERVATIONS */}
          {activeTab === 'booking' && (
            <div className="max-w-5xl mx-auto bg-white p-8 rounded-[2.5rem] shadow-lg border border-slate-100 animate-in fade-in">
              <h2 className="text-2xl font-black mb-6 flex items-center gap-2"><FaListAlt className="text-purple-600" /> Liste des Réservations</h2>
              {reservations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-slate-100 text-xs text-slate-400 uppercase tracking-widest">
                        <th className="py-4 px-2">Client</th>
                        <th className="py-4 px-2">Arrivée</th>
                        <th className="py-4 px-2">Départ</th>
                        <th className="py-4 px-2">Statut</th>
                        <th className="py-4 px-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm font-bold">
                      {reservations.map(res => (
                        <tr key={res.id} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-4 px-2">{res.guest_name || 'Anonyme'}</td>
                          <td className="py-4 px-2">{res.start_at ? new Date(res.start_at).toLocaleDateString() : '-'}</td>
                          <td className="py-4 px-2">{res.end_at ? new Date(res.end_at).toLocaleDateString() : '-'}</td>
                          <td className="py-4 px-2">
                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${res.status === 'confirmed' ? 'bg-green-100 text-green-700' : res.status === 'blocked' ? 'bg-slate-200 text-slate-600' : 'bg-yellow-100 text-yellow-700'}`}>
                              {res.status}
                            </span>
                          </td>
                          <td className="py-4 px-2 text-right">
                            <button onClick={() => setSelectedRes(res)} className="bg-blue-50 text-blue-600 px-4 py-2 rounded-xl hover:bg-blue-100 transition text-xs uppercase tracking-widest">Détails</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="text-center py-12 text-slate-400 font-bold">Aucune réservation.</div>}
            </div>
          )}

          {/* ✅ MOTEUR UPSELL */}
          {activeTab === 'upsell' && (
            <div className="max-w-6xl mx-auto animate-in fade-in">
              <UpsellEngineSection />
            </div>
          )}

          {/* STYLE CSS GLOBALES */}
          <style>{`
            .label { display: block; font-size: 0.75rem; font-weight: 900; text-transform: uppercase; letter-spacing: 0.1em; color: #94a3b8; margin-bottom: 0.5rem; padding-left: 0.25rem; }
            .input-field { width: 100%; padding: 1rem; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 1rem; font-weight: 600; color: #0f172a; transition: all; }
            .input-field:focus { outline: none; border-color: #3b82f6; background-color: white; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }

            .animate-pulse-slow { animation: pulseSlow 1.6s ease-in-out infinite; }
            @keyframes pulseSlow { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.02); opacity: 0.92; } }

            .toggle { width: 44px; height: 24px; background: #cbd5e1; border-radius: 999px; position: relative; transition: background .2s ease; }
            .toggle-on { background: #34d399; }
            .toggle-knob { width: 20px; height: 20px; background: #fff; border-radius: 999px; position: absolute; top: 2px; left: 2px; transition: left .2s ease; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
            .toggle-on .toggle-knob { left: 22px; }

            .animate-blob {
              animation: blob 7s infinite;
            }
            .animation-delay-2000 {
              animation-delay: 2s;
            }
            @keyframes blob {
              0% { transform: translate(0px, 0px) scale(1); }
              33% { transform: translate(30px, -50px) scale(1.1); }
              66% { transform: translate(-20px, 20px) scale(0.9); }
              100% { transform: translate(0px, 0px) scale(1); }
            }
          `}</style>
        </main>
      </div>

      <Footer />
    </div>
  );
}
